import qs from "qs";
import i18n from "i18next";
import queryString from "query-string";
import { differenceInCalendarDays } from "date-fns";
import apiClient from "../api/client";
import "../i18n";

import { listing } from "../constants/actionTypes";
import {
  editStatuses,
  getNotificationCategoriesForKind,
  isApiV2EditorPath,
  listingProfessionType,
  listFilterOptions,
  listingScalarAutosaveFields,
  listingUnsavedSectionStatus,
  resolveDashboardListingId,
  resolveListingApiName,
  resolveListingIdFromDetailPath,
  resolveListingTabPath,
  statuses as listingStatuses
} from "../constants/listings";
import { actions as homeActions } from "./home";
import { canWriteListingsInTeamContext, resolveFinancialTeamOwnerId, resolveListingTeamContext } from "../helpers/listingTeamContext";
import { allocateAutosaveSequence, getAutosaveClientId } from "../helpers/autosaveSequence";
import { buildAutosaveSignature, findDuplicateAutosave, trackLatestAutosave } from "../helpers/autosaveDuplicates";
import { resolveApiErrorMessage } from "../helpers/resolveApiErrorMessage";
import { LISTING_CONFIRM_CODE_COPY, listingConfirmCodeFailPayload } from "../helpers/verificationConfirmError";
import { getPublishErrorMessage } from "../helpers/getPublishErrorMessage";
import { track } from "../helpers/track";
import { isCanceledRequest } from "../helpers/isCanceledRequest";
import { eventName } from "../constants/track";

const pendingMembershipRequests = new Map();
const pendingNotificationDefaultRequests = new Map();
/** One in-flight toggle request per notification-type row and field. */
const inFlightNotificationTypeRequests = new Map(); // `${rowId}:${field}` -> AbortController
const pendingMemberDataRequests = new Map();
const memberDataFetchGeneration = new Map();
const autosaveGeneration = new Map();
const serializedAutosaveQueues = new Map();
const autosaveThunkInflight = new Map();

const beginAutosaveThunk = scope => {
  autosaveThunkInflight.set(scope, (autosaveThunkInflight.get(scope) || 0) + 1);
};

const endAutosaveThunk = scope => {
  const remaining = (autosaveThunkInflight.get(scope) || 1) - 1;
  if (remaining <= 0) {
    autosaveThunkInflight.delete(scope);
  } else {
    autosaveThunkInflight.set(scope, remaining);
  }
};

const nextAutosaveSeq = async scope => {
  const generation = await allocateAutosaveSequence(scope);
  autosaveGeneration.set(scope, generation);
  return generation;
};

const buildAutosaveOrderingMeta = (section, memberData, generation) => ({
  autosave_seq: generation,
  autosave_client_id: getAutosaveClientId(),
  autosave_section: section,
  autosave_base_version: Number(memberData?.autosave_versions?.[section] ?? 0)
});

const resolveAutosaveVersionFromResponse = res => {
  if (res?.data?.autosave_version != null) {
    return Number(res.data.autosave_version);
  }
  const payload = res?.data?.data;
  if (payload?._autosave_version != null) {
    return Number(payload._autosave_version);
  }
  if (payload?.listing?._autosave_version != null) {
    return Number(payload.listing._autosave_version);
  }
  if (res?.data?._autosave_version != null) {
    return Number(res.data._autosave_version);
  }
  return null;
};

const withUpdatedAutosaveVersion = (memberData, section, version) => {
  if (version == null || !section) {
    return memberData || {};
  }
  return {
    ...(memberData || {}),
    autosave_versions: {
      ...((memberData && memberData.autosave_versions) || {}),
      [section]: version
    }
  };
};

const resolveStaleReasonFromResponse = err => err?.response?.data?.reason || "superseded";

const handleAutosaveConflict = (dispatch, refetchAction, reason, section) => {
  dispatch(refetchAction);

  if (reason === "conflict") {
    dispatch({
      type: listing.SUBMIT_CHANGES_FAIL,
      payload: "Not saved - this listing was changed elsewhere. Your latest edit was not applied.",
      section
    });
  }
};

const pendingAutosaveListeners = new Set();

const notifyPendingAutosaves = () => {
  const pending = serializedAutosaveQueues.size > 0;
  pendingAutosaveListeners.forEach(listener => listener(pending));
};

export const subscribePendingAutosaves = listener => {
  pendingAutosaveListeners.add(listener);
  listener(serializedAutosaveQueues.size > 0);
  return () => {
    pendingAutosaveListeners.delete(listener);
  };
};

const enqueueLatestSerializedAutosave = (scope, runRequest) => {
  let queue = serializedAutosaveQueues.get(scope);

  if (!queue) {
    queue = {
      inFlight: false,
      queuedRequest: null,
      queuedDeferreds: []
    };
    serializedAutosaveQueues.set(scope, queue);
    notifyPendingAutosaves();
  }

  const runQueuedRequest = () => {
    if (!queue.queuedRequest) {
      queue.inFlight = false;
      serializedAutosaveQueues.delete(scope);
      notifyPendingAutosaves();
      return;
    }

    const nextRequest = queue.queuedRequest;
    const deferreds = queue.queuedDeferreds;

    queue.queuedRequest = null;
    queue.queuedDeferreds = [];
    queue.inFlight = true;

    Promise.resolve()
      .then(nextRequest)
      .then(result => {
        deferreds.forEach(({ resolve }) => resolve(result));
      })
      .catch(error => {
        deferreds.forEach(({ reject }) => reject(error));
      })
      .finally(runQueuedRequest);
  };

  if (queue.inFlight) {
    return new Promise((resolve, reject) => {
      queue.queuedRequest = runRequest;
      queue.queuedDeferreds.push({ resolve, reject });
    });
  }

  queue.inFlight = true;

  return Promise.resolve()
    .then(runRequest)
    .finally(runQueuedRequest);
};

export const hasPendingAutosaves = scopePrefix => {
  if (!scopePrefix) {
    return serializedAutosaveQueues.size > 0;
  }
  return [...serializedAutosaveQueues.keys()].some(scope => String(scope).startsWith(scopePrefix));
};

export const awaitPendingAutosaves = ({ timeoutMs = 15000 } = {}) =>
  new Promise(resolve => {
    const startedAt = Date.now();
    const tick = () => {
      if (!serializedAutosaveQueues.size || Date.now() - startedAt >= timeoutMs) {
        resolve();
        return;
      }
      setTimeout(tick, 50);
    };
    tick();
  });

/** Map of in-flight autosave promise → full scope (`apiPrefix:owner:listing:section`). */
const inflightAutosaves = new Map();

/**
 * Scopes whose last persist attempt failed (not cancel / not superseded).
 * Cleared only when that same scope succeeds — never by another section's save,
 * and never at publish/validation start.
 */
const failedAutosaveScopes = new Set();

/**
 * Map a listing id to the dashboard (parent) id, but only when `memberData.parent_id`
 * belongs to that same listing. An explicit id from another listing (route or field)
 * must not be rewritten by stale member data during A → B navigation (SEC-012).
 *
 * @param {string|number} rawId
 * @param {Object} [memberData]
 * @returns {string}
 */
const resolveAutosaveDashboardListingId = (rawId, memberData) => {
  const memberListingId = memberData?.listing_id;
  const memberParentId = memberData?.parent_id;
  const parentIdBelongsToRawId =
    memberParentId != null && (String(rawId) === String(memberListingId) || String(rawId) === String(memberParentId));
  return resolveDashboardListingId(rawId, parentIdBelongsToRawId ? memberParentId : null) || rawId || "";
};

/**
 * Prefix shared by all section scopes for one listing/team editor:
 * `${apiPrefix}:${ownerId}:${listingId}:`
 *
 * An explicit `listingId` (route id) must not be rewritten via `memberData.parent_id`
 * unless it is the currently loaded listing's own id or parent. Otherwise a navigate
 * from A → B keeps A's parent while memberData is still A (SEC-012).
 */
export const buildAutosaveListingScopePrefix = (getState, pathname, listingId) => {
  const {
    listing: { memberData }
  } = getState();
  const { apiPrefix, userIdParams } = resolveListingTeamContext(getState, pathname);
  const rawId = listingId ?? memberData?.listing_id ?? "";
  const resolvedId = resolveAutosaveDashboardListingId(rawId, memberData);
  return `${apiPrefix}:${userIdParams.user_id || ""}:${resolvedId}:`;
};

/**
 * Scope prefixes whose editor is currently open, with a mount count so a remount that
 * overlaps the previous unmount stays registered.
 */
const openAutosaveScopePrefixes = new Map();

/**
 * Mark a listing/team editor as open for as long as it is mounted; returns the release fn.
 * A request can outlive the editor that started it, so a failure landing after release must
 * not re-add a scope the editor already cleared — that scope has no owner left to retry it
 * and would block publishing forever (SEC-010).
 */
export const registerOpenAutosaveScopePrefix = scopePrefix => {
  if (!scopePrefix) {
    return () => {};
  }

  openAutosaveScopePrefixes.set(scopePrefix, (openAutosaveScopePrefixes.get(scopePrefix) || 0) + 1);

  let isReleased = false;
  return () => {
    if (isReleased) {
      return;
    }
    isReleased = true;
    const remaining = (openAutosaveScopePrefixes.get(scopePrefix) || 0) - 1;
    if (remaining > 0) {
      openAutosaveScopePrefixes.set(scopePrefix, remaining);
    } else {
      openAutosaveScopePrefixes.delete(scopePrefix);
    }
  };
};

const isAutosaveScopeEditorOpen = scope => [...openAutosaveScopePrefixes.keys()].some(prefix => scope.startsWith(prefix));

const clearAutosavePersistFailure = scope => {
  failedAutosaveScopes.delete(scope);
};

const noteAutosavePersistFailure = scope => {
  if (!isAutosaveScopeEditorOpen(scope)) {
    return;
  }
  failedAutosaveScopes.add(scope);
};

// Statuses a retry may fix (session, missing listing, stale save, timeout, rate limit) keep their own handling.
const retryableClientErrorStatuses = [401, 404, 408, 409, 429];

/**
 * The server's reason for refusing an autosave (GROWTH-8026): api-v2 answers a refused value with a 4xx and
 * { message, errors }. Retrying the same value cannot fix it, so the reason is shown instead of Retry.
 *
 * The caller asks for it by setting `onRefused(reason)` on the AbortController it passes (the argument list is unchanged).
 *
 * @param {object} err Axios-style error
 * @returns {string} The reason, or "" when the failure is not a refusal with a text reason
 */
const resolveAutosaveRefusal = err => {
  const status = err?.response?.status;
  if (!(status >= 400 && status < 500) || retryableClientErrorStatuses.includes(status)) {
    return "";
  }
  const reason = resolveApiErrorMessage(err, "");
  return typeof reason === "string" ? reason.trim() : "";
};

/**
 * 409 stale handling for publish gate (SEC-008):
 * - conflict: real persist loss — keep blocking until that section saves successfully
 * - superseded: refetch reconciles — clear so Publish is not stuck
 */
const applyStaleAutosavePersistOutcome = (scope, reason) => {
  if (reason === "conflict") {
    noteAutosavePersistFailure(scope);
  } else {
    clearAutosavePersistFailure(scope);
  }
};

/** Drop remembered persist failures for one listing/team (editor leave / navigate away). */
export const clearAutosavePersistFailuresForPrefix = scopePrefix => {
  if (!scopePrefix) {
    return;
  }
  [...failedAutosaveScopes].forEach(scope => {
    if (scope.startsWith(scopePrefix)) {
      failedAutosaveScopes.delete(scope);
    }
  });
};

const hasAutosavePersistFailures = scopePrefix => {
  if (!scopePrefix) {
    return failedAutosaveScopes.size > 0;
  }
  return [...failedAutosaveScopes].some(scope => scope.startsWith(scopePrefix));
};

const collectInflightAutosaves = scopePrefix => {
  const work = [];
  inflightAutosaves.forEach((scope, promise) => {
    if (!scopePrefix || scope.startsWith(scopePrefix)) {
      work.push(promise);
    }
  });
  return work;
};

const trackAutosaveWork = (promise, scope) => {
  inflightAutosaves.set(promise, scope || "");
  // `then(forget, forget)`, not `finally`: `finally` would return a second promise that rejects
  // with the save's error and nothing handles it.
  const forget = () => {
    inflightAutosaves.delete(promise);
  };
  promise.then(forget, forget);
  return promise;
};

const delayMs = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Await in-flight autosave / submitChanges thunks until quiescent.
 * When `scopePrefix` is set, only that listing/team's work and failures count.
 * Work is registered synchronously on dispatch (before seq allocation).
 * Resolves to { ok: true } when quiet and no persist failures.
 * Resolves to { ok: false, reason: "timeout"|"persist" } if timed out with
 * remaining work or a save failed — callers must not publish. Never rejects.
 */
export const awaitAutosaveQuiescence = async ({ timeoutMs = 15000, scopePrefix } = {}) => {
  const deadline = Date.now() + timeoutMs;

  const runPass = () => {
    const work = collectInflightAutosaves(scopePrefix);
    if (!work.length) {
      return Promise.resolve(true);
    }

    const remaining = deadline - Date.now();
    if (remaining <= 0) {
      return Promise.resolve(false);
    }

    return Promise.race([Promise.allSettled(work), delayMs(remaining)]).then(() => {
      if (!collectInflightAutosaves(scopePrefix).length) {
        return true;
      }
      if (Date.now() >= deadline) {
        return false;
      }
      return runPass();
    });
  };

  return runPass().then(quiescent => {
    if (!quiescent) {
      return { ok: false, reason: "timeout" };
    }
    if (hasAutosavePersistFailures(scopePrefix)) {
      return { ok: false, reason: "persist" };
    }
    return { ok: true };
  });
};

const AUTOSAVE_QUIESCENCE_MESSAGES = {
  timeout: "Still saving your changes. Please try publishing again in a moment.",
  persist: "Could not save your latest changes. Please try again before publishing."
};

export const getAutosaveQuiescenceMessage = reason => AUTOSAVE_QUIESCENCE_MESSAGES[reason] || AUTOSAVE_QUIESCENCE_MESSAGES.persist;

const buildListingApiUrl = (path, getState, queryParams = {}, pathname, search) => {
  const { apiPrefix, userIdParams } = resolveListingTeamContext(getState, pathname, search);
  const query = queryString.stringify({ ...queryParams, ...userIdParams }, { skipNull: true, skipEmptyString: true });

  return `${apiPrefix}${path}${query ? `?${query}` : ""}`;
};

const withListingTeamBody = (getState, data = {}, pathname, search) => {
  const { userIdParams } = resolveListingTeamContext(getState, pathname, search);

  return { ...data, ...userIdParams };
};

/** The rows a listing's first visit saves: each category its kind can receive, email on, SMS off (GROWTH-6541). */
const buildDefaultNotificationRows = (listingId, listingKind) =>
  getNotificationCategoriesForKind(listingKind).map(category => ({
    listing_id: listingId,
    category,
    is_email: 1,
    is_sms: 0
  }));

const buildResetDraftRequest = (getState, { pathname, listingId }) => {
  const { apiPrefix, userIdParams } = resolveListingTeamContext(getState, pathname);
  const userIdQuery = userIdParams.user_id ? `&user_id=${userIdParams.user_id}` : "";
  const userIdBody = userIdParams.user_id ? { user_id: userIdParams.user_id } : {};
  const jsonApiTeamPrefix = userIdParams.user_id ? "/team" : "";
  const listingName = resolveListingApiName(pathname);

  if (listingName) {
    return {
      method: "delete",
      url: `${apiPrefix}/${listingName}?listing_id=${listingId}&command=reset${userIdQuery}`,
      headers: { "Content-Type": "application/json" }
    };
  }

  return {
    method: "post",
    url: `/members/json-api${jsonApiTeamPrefix}/listing/delete-draft`,
    data: qs.stringify({ listing_id: listingId, ...userIdBody }),
    headers: { "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8" }
  };
};

/**
 * Delete listing request: api-v2 DELETE for profiles/jobs/events/organizations,
 * legacy json-api POST for /listings/:id.
 */
const buildDeleteMembershipRequest = (getState, { pathname, listingId }) => {
  const { apiPrefix, userIdParams } = resolveListingTeamContext(getState, pathname);
  const userIdQuery = userIdParams.user_id ? `&user_id=${userIdParams.user_id}` : "";
  const userIdBody = userIdParams.user_id ? { user_id: userIdParams.user_id } : {};
  const jsonApiTeamPrefix = userIdParams.user_id ? "/team" : "";
  const listingName = resolveListingApiName(pathname);

  if (listingName) {
    return {
      listingName,
      method: "delete",
      url: `${apiPrefix}/${listingName}?listing_id=${listingId}${userIdQuery}`,
      headers: { "Content-Type": "application/json" }
    };
  }

  return {
    listingName: null,
    method: "post",
    url: `/members/json-api${jsonApiTeamPrefix}/listing/delete`,
    data: qs.stringify({ listing_id: listingId, ...userIdBody }),
    headers: { "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8" }
  };
};

const buildListingCommandRequest = (getState, { pathname, listingId, command, legacyJsonApiPath }) => {
  const { apiPrefix, userIdParams } = resolveListingTeamContext(getState, pathname);
  const jsonApiTeamPrefix = userIdParams.user_id ? "/team" : "";
  const listingName = resolveListingApiName(pathname);
  const payload = withListingTeamBody(getState, { listing_id: listingId, command }, pathname);

  if (listingName) {
    return {
      method: "put",
      url: `${apiPrefix}/${listingName}`,
      data: payload,
      headers: { "Content-Type": "application/json" }
    };
  }

  return {
    method: "post",
    url: `/members/json-api${jsonApiTeamPrefix}/${legacyJsonApiPath}`,
    data: qs.stringify({ listing_id: listingId, command, ...userIdParams }),
    headers: {
      "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8"
    }
  };
};

/**
 * GET for the Upgrade / Renew / Pay-pending modals: json-api, sent under /team with the owner's user_id when a team
 * Admin buys for the owner's listing (GROWTH-7126); otherwise exactly the personal request.
 */
const buildFinancialJsonApiGet = (getState, path, params) => {
  const ownerId = resolveFinancialTeamOwnerId(getState);

  return {
    method: "get",
    responseType: "json",
    url: `/members/json-api${ownerId ? "/team" : ""}/${path}`,
    params: ownerId ? { ...params, user_id: ownerId } : params
  };
};

const buildExpandParam = (...parts) =>
  [
    ...new Set(
      parts
        .flatMap(part => String(part || "").split(","))
        .map(item => item.trim())
        .filter(Boolean)
    )
  ].join(",");

const getListingTypeFromPathname = pathname => {
  if (pathname.includes("/profile")) return "profile";
  if (pathname.includes("/my-organizations")) return "organization";
  if (pathname.includes("/my-events")) return "event";
  if (pathname.includes("/my-jobs")) return "job";
  return "";
};

const getListingExpandForTab = (expand, pathname) => {
  const listingType = getListingTypeFromPathname(pathname);
  const currentTab = resolveListingTabPath(pathname);
  const tabExpandsByListingType = {
    profile: {
      details: [
        "additional_images",
        "associations",
        "bookmeetings",
        "communities",
        "education",
        "insurances",
        "languages",
        "parent_company",
        "practices",
        "socials",
        "tags"
      ],
      bio: ["text_langs"],
      licenses: ["licenses"],
      location: ["lawfirms"],
      publications: ["directories_availability"],
      messages: [],
      notifications: ["verifiedphones", "verifiedemails"]
    },
    organization: {
      details: [
        "additional_images",
        "associations",
        "bookmeetings",
        "communities",
        "education",
        "insurances",
        "languages",
        "parent_company",
        "practices",
        "socials",
        "tags"
      ],
      bio: ["text_langs"],
      licenses: ["licenses"],
      location: ["lawfirms"],
      publications: ["directories_availability"],
      donations: [],
      messages: [],
      notifications: ["verifiedphones", "verifiedemails"]
    },
    event: {
      details: [
        "additional_images",
        "bookmeetings",
        "communities",
        "education",
        "event_schedules",
        "event_tickets",
        "languages",
        "parent_company",
        "practices",
        "socials",
        "tags"
      ],
      bio: ["text_langs"],
      licenses: [],
      location: ["lawfirms"],
      publications: ["directories_availability"],
      messages: [],
      notifications: ["verifiedphones", "verifiedemails"]
    },
    job: {
      details: [
        "additional_images",
        "associations",
        "bookmeetings",
        "education",
        "insurances",
        "jobs",
        "languages",
        "parent_company",
        "practices",
        "socials",
        "tags"
      ],
      bio: ["text_langs"],
      licenses: ["licenses"],
      location: ["lawfirms"],
      publications: ["directories_availability"],
      messages: [],
      notifications: ["verifiedphones", "verifiedemails"]
    }
  };

  const tabExpands = tabExpandsByListingType[listingType];
  if (!tabExpands) return expand;

  const tabRequired = tabExpands[currentTab] || tabExpands.details || [];
  const allowedListingExpands = new Set(tabRequired);
  const fromUserPreference = String(expand || "")
    .split(",")
    .map(item => item.trim())
    .filter(item => allowedListingExpands.has(item));

  return [...new Set([...tabRequired, ...fromUserPreference])].join(",");
};

const getListingDefaultExpandForPathname = pathname => {
  const listingType = getListingTypeFromPathname(pathname);
  const currentTab = resolveListingTabPath(pathname);
  const listingTabs = new Set(["profile", "organization", "event", "job"]);

  if (!listingTabs.has(listingType)) {
    return ["filling_options", "validations"].join(",");
  }

  const tabDefaultsMap = {
    details: ["filling_options", "validations"],
    bio: ["filling_options", "validations", "text_langs"],
    licenses: ["filling_options", "validations"],
    location: ["filling_options", "validations"],
    donations: ["filling_options", "validations"],
    messages: ["filling_options", "validations"],
    notifications: ["filling_options", "validations", "verifiedphones", "verifiedemails"],
    publications: ["filling_options", "validations", "directories_availability"],
    default: ["filling_options", "validations"]
  };

  return (tabDefaultsMap[currentTab] || tabDefaultsMap.default).join(",");
};

const getForcedExpandForPathname = (pathname, options = {}) => {
  if (options.forceFullExpand) {
    return "directories_availability";
  }

  const currentTab = resolveListingTabPath(pathname);
  return currentTab === "publications" ? "directories_availability" : "";
};

/**
 * save_social only returns rows touched in this request. Redux must keep the full list
 * the user submitted; merge server rows (e.g. prkey) into each sent row by name/prkey.
 */
const mergeSocialsAfterAutosave = (sentSocials, serverReturned) => {
  const sent = Array.isArray(sentSocials) ? sentSocials : [];
  const server = Array.isArray(serverReturned) ? serverReturned : [];

  if (sent.length === 0) {
    return [];
  }

  return sent.map(sentRow => {
    const byPrkey = sentRow.prkey != null ? server.find(s => s.prkey != null && String(s.prkey) === String(sentRow.prkey)) : null;
    const byName = server.find(s => s.social_name === sentRow.social_name);
    const match = byPrkey || byName;
    return match ? { ...sentRow, ...match } : { ...sentRow };
  });
};

/**
 * save_language returns rows touched in this request. Redux must keep the full list
 * the user submitted; merge server rows (e.g. prkey, type) into each sent row.
 */
const mergeInsurancesAfterAutosave = (sentInsurances, serverReturned) => {
  const sent = Array.isArray(sentInsurances) ? sentInsurances : [];
  const server = Array.isArray(serverReturned) ? serverReturned : [];

  if (sent.length === 0) {
    return server;
  }

  if (server.length === 0) {
    return sent;
  }

  return sent.map(sentRow => {
    const byPrkey = sentRow.prkey != null ? server.find(s => s.prkey != null && String(s.prkey) === String(sentRow.prkey)) : null;
    const bySignature = server.find(s => {
      const fields = ["company_id", "network_id", "plan_id", "other_company", "other_network", "other_plan"];
      return fields.every(field => String(s[field] ?? "") === String(sentRow[field] ?? ""));
    });
    const match = byPrkey || bySignature;

    return match
      ? {
          ...match,
          ...sentRow,
          prkey: match.prkey ?? sentRow.prkey
        }
      : { ...sentRow };
  });
};

const mergeLanguagesAfterAutosave = (sentLanguages, serverReturned) => {
  const sent = Array.isArray(sentLanguages) ? sentLanguages : [];
  const server = Array.isArray(serverReturned) ? serverReturned : [];

  if (sent.length === 0) {
    return server;
  }

  if (server.length === 0) {
    return sent;
  }

  return sent.map(sentRow => {
    const byPrkey = sentRow.prkey != null ? server.find(s => s.prkey != null && String(s.prkey) === String(sentRow.prkey)) : null;
    const byLanguageId = server.find(s => String(s.language_id) === String(sentRow.language_id));
    const match = byPrkey || byLanguageId;
    return match
      ? {
          ...match,
          ...sentRow,
          prkey: match.prkey ?? sentRow.prkey,
          type: sentRow.type ?? match.type,
          skill: sentRow.skill ?? match.skill,
          language_id: sentRow.language_id ?? match.language_id
        }
      : { ...sentRow };
  });
};

const normalizeDirectMessages = (data = {}, fallbackListingId = null) => {
  const normalizeInt = (value, defaultValue) => {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? defaultValue : parsed;
  };

  const phoneRequirement = typeof data.phone_requirement === "string" ? data.phone_requirement.toLowerCase() : "optional";

  return {
    id: data.id !== undefined && data.id !== null ? Number(data.id) : null,
    listing_id: data.listing_id !== undefined && data.listing_id !== null ? Number(data.listing_id) : fallbackListingId,
    is_enabled: normalizeInt(data.is_enabled, 1),
    email_verification: normalizeInt(data.email_verification, 1),
    phone_requirement: phoneRequirement === "required" ? "required" : "optional",
    phone_verification: normalizeInt(data.phone_verification, 0),
    is_sponsored: normalizeInt(data.is_sponsored, 0) === 1,
    persisted: data.persisted !== undefined ? Boolean(data.persisted) : Boolean(data.id)
  };
};

// GROWTH-7696: keep the HTTP status next to the API body so the row can show a 403's own message.
const notificationTypeRollbackError = error => {
  const data = error.response?.data;
  if (data && typeof data === "object") {
    return { ...data, status: error.response.status };
  }
  return data || error.message || true;
};

const notificationTypeFieldKey = (id, field) => `${String(id)}:${field}`;

// GROWTH-7124: upgrade-options, renew-options and listing/amount answer 400 without an id.
const hasListingId = id => id != null && id !== "" && Number(id) !== 0;

const rejectMissingListingId = dispatch => {
  dispatch({
    type: listing.GET_UPGRADE_OPTIONS_FAIL,
    payload: "The listing has not loaded yet. Close this window and try again."
  });
  return Promise.resolve();
};

export const actions = {
  resetDraft(listingId, pathname) {
    return (dispatch, getState) => {
      const requestConfig = buildResetDraftRequest(getState, { pathname, listingId });
      const listingName = resolveListingApiName(pathname);

      dispatch(actions.setLoading("memberData", true));

      return apiClient({
        responseType: "json",
        ...requestConfig
      })
        .then(res => {
          // apiv2 wraps the listing in res.data.data; the legacy json-api ajaxResponse
          // encodes the listing object directly as res.data, so fall back accordingly.
          const data = res.data.data || res.data;

          if (listingName) {
            const routeListingId = resolveListingIdFromDetailPath(pathname);
            const refetchListingId = data?.parent_id || routeListingId || listingId;

            return dispatch(
              actions.getMemberDataNew(refetchListingId, pathname, null, false, {
                bustCache: true
              })
            );
          }

          dispatch({
            type: listing.GET_MEMBER_DATA,
            payload: { data, help: res.data._help }
          });
          return data;
        })
        .catch(() => {
          dispatch(actions.setLoading("memberData", false));
          dispatch({
            type: listing.RESET_DRAFT_FAIL,
            payload: "Error resetting draft"
          });
        });
    };
  },

  getMemberDataNew(listingId, pathname, navigate, successMessage = false, options = {}) {
    return (dispatch, getState) => {
      const {
        listing: { expand }
      } = getState();

      const { apiPrefix, userIdParams: listingTeamQueryParams } = resolveListingTeamContext(getState, pathname);
      const ownerIdAtRequest = listingTeamQueryParams.user_id ? String(listingTeamQueryParams.user_id) : "";
      const userIdQuery = queryString.stringify(listingTeamQueryParams, { skipNull: true, skipEmptyString: true });
      const userIdParams = userIdQuery ? `&${userIdQuery}` : "";

      const isProfile = pathname.includes("/profile");
      const isOrganization = pathname.includes("/my-organizations");
      const isEvents = pathname.includes("/my-events");
      const isJobs = pathname.includes("/my-jobs");
      const listingName = resolveListingApiName(pathname);

      if (!listingName) {
        return Promise.resolve(null);
      }
      const expandDefault = getListingDefaultExpandForPathname(pathname);
      const updatedExpander = expand.replace("images", "additional_images");
      const shouldOptimizeExpands = (isProfile || isOrganization || isEvents || isJobs) && !options.forceFullExpand;
      const optimizedExpand = shouldOptimizeExpands ? getListingExpandForTab(updatedExpander, pathname) : updatedExpander;
      const id = isProfile ? "?" : `?id=${listingId}&`;
      // `options.expand`: expands the caller needs whatever the tab (the Listing Editor v2 loads the listing once for every page).
      const expandParam = buildExpandParam(expandDefault, optimizedExpand, getForcedExpandForPathname(pathname, options), options.expand);
      const cacheNonce = Date.now();
      const url = `${apiPrefix}/${listingName}/get${id}expand=${expandParam}${userIdParams}&_=${cacheNonce}`;
      const listingIdAtRequest = getState().listing.memberData?.listing_id;
      const fetchScope = `${ownerIdAtRequest}:${listingId}:${pathname}`;
      // Opt-in (GROWTH-7611): the URL carries a Date.now() nonce, so two bust-cache calls never share a key. A caller that
      // can overlap itself asks to join an identical in-flight request instead. Checked before the generation bump below,
      // otherwise the joined call would mark the first request stale and nothing would reach the store.
      const dedupeKey = options.bustCache && options.dedupeInFlight ? `member-data:bust:${fetchScope}:${expandParam}` : null;
      if (dedupeKey && pendingMemberDataRequests.has(dedupeKey)) {
        return pendingMemberDataRequests.get(dedupeKey);
      }
      const generation = (memberDataFetchGeneration.get(fetchScope) || 0) + 1;
      memberDataFetchGeneration.set(fetchScope, generation);
      const requestKey = dedupeKey || (options.bustCache ? `member-data:${url}` : `member-data:${fetchScope}:${generation}`);

      if (pendingMemberDataRequests.has(requestKey)) {
        return pendingMemberDataRequests.get(requestKey);
      }

      if (!options.skipLoading) {
        dispatch(actions.setLoading("memberData", true));
      }

      const request = apiClient({
        method: "get",
        responseType: "json",
        url
      })
        .then(res => {
          const { userIdParams: teamQueryNow } = resolveListingTeamContext(getState, pathname);
          const ownerIdNow = teamQueryNow.user_id ? String(teamQueryNow.user_id) : "";
          if (ownerIdAtRequest !== ownerIdNow) {
            return res.data?.data ?? null;
          }

          const payloadData = res.data?.data;
          const isListingPayloadObject = payloadData && !Array.isArray(payloadData);
          const hasListingRecord = isProfile || (isListingPayloadObject && payloadData.listing_id != null);
          const listingIdNow = getState().listing.memberData?.listing_id;
          const isStaleGeneration = memberDataFetchGeneration.get(fetchScope) !== generation;
          const responseMatchesRequestedListing =
            payloadData?.listing_id != null &&
            (String(payloadData.listing_id) === String(listingId) ||
              (payloadData.parent_id != null && String(payloadData.parent_id) === String(listingId)));
          const needsInitialHydration =
            hasListingRecord && responseMatchesRequestedListing && (listingIdNow == null || listingIdNow === "");

          if (isStaleGeneration && !needsInitialHydration) {
            return payloadData ?? null;
          }

          if (payloadData && hasListingRecord) {
            const responseListingId = payloadData.listing_id;
            const activeRouteListingId = typeof window !== "undefined" ? resolveListingIdFromDetailPath(window.location.pathname) : null;
            const responseMatchesActiveRoute =
              activeRouteListingId != null &&
              (String(responseListingId) === String(activeRouteListingId) ||
                (payloadData.parent_id != null && String(payloadData.parent_id) === String(activeRouteListingId)));

            if (
              !isProfile &&
              activeRouteListingId != null &&
              String(activeRouteListingId) !== String(listingId) &&
              !responseMatchesActiveRoute
            ) {
              return payloadData ?? null;
            }

            const isStaleProfileGetResponse =
              isProfile &&
              listingIdAtRequest != null &&
              listingIdNow != null &&
              responseListingId != null &&
              String(listingIdAtRequest) !== String(listingIdNow) &&
              String(responseListingId) === String(listingIdAtRequest);

            if (!isStaleProfileGetResponse) {
              dispatch({
                type: listing.GET_MEMBER_DATA,
                payload: {
                  data: { ...payloadData, languages: payloadData.languages || [] },
                  help: { ...payloadData.filling_options, validations: payloadData.validations },
                  successMessage
                }
              });
            }
            return payloadData;
          }

          if (!isProfile && (!isListingPayloadObject || payloadData.listing_id == null)) {
            return null;
          }

          dispatch({
            type: listing.SET_DEFAULT_MEMBER_DATA
          });
          return null;
        })
        .catch(err => {
          const { userIdParams: teamQueryNow } = resolveListingTeamContext(getState, pathname);
          const ownerIdNow = teamQueryNow.user_id ? String(teamQueryNow.user_id) : "";
          if (ownerIdAtRequest !== ownerIdNow) {
            return null;
          }

          if (err?.response?.data?.error === "Not found" && navigate) {
            navigate("/404");
          } else {
            dispatch({
              type: listing.GET_MEMBER_DATA_FAIL,
              payload: err.response
            });
          }
          return null;
        })
        .finally(() => {
          pendingMemberDataRequests.delete(requestKey);
          if (!options.skipLoading) {
            const { userIdParams: teamQueryNow } = resolveListingTeamContext(getState, pathname);
            const ownerIdNow = teamQueryNow.user_id ? String(teamQueryNow.user_id) : "";
            if (ownerIdAtRequest === ownerIdNow) {
              dispatch(actions.setLoading("memberData", false));
            }
          }
        });

      pendingMemberDataRequests.set(requestKey, request);
      return request;
    };
  },

  // options.skipMemberData (GROWTH-7611): the editor always passes it, because its tab effect already fetches the listing
  // (the profile editor included); without it the reply chains its own listing fetch, as before.
  getValidations(listingId, pathname, navigate, options = {}) {
    return dispatch => {
      const listingName = resolveListingApiName(pathname);

      apiClient({
        method: "get",
        responseType: "json",
        url: `/members/api-v2/validations?command=${listingName}`
      })
        .then(res => {
          const expand = res.data.data
            .filter(item => item.enabled && item.expand_name)
            .map(item => item.expand_name)
            .join(",")
            .replaceAll(" ", "");

          dispatch({
            type: listing.GET_VALIDATIONS,
            payload: expand
          });

          if (!options.skipMemberData) {
            dispatch(actions.getMemberDataNew(listingId, pathname, navigate));
          }
        })
        .catch(err => {
          dispatch({
            type: listing.GET_VALIDATIONS_FAIL,
            payload: err
          });
        });
    };
  },

  setLoading(section, isSectionLoading) {
    return {
      type: listing.SET_LOADING,
      payload: { section, isSectionLoading }
    };
  },

  setUnsavedSection(section, status, listingScope) {
    return {
      type: listing.SET_UNSAVED_SECTION,
      payload: { section, status, listingScope }
    };
  },

  updateActiveUpgradeOptions(activeOption) {
    return {
      type: listing.UPDATE_ACTIVE_UPGRADE_OPTIONS,
      payload: activeOption
    };
  },

  closeAllModals() {
    return {
      type: listing.SUBMIT_CHANGES_CLOSE_MODALS
    };
  },

  // GROWTH-6535: the only remaining writer to update-emails-phones. Arrays are not accepted;
  // the endpoint still flips leademail_primary_only when those keys are omitted.
  submitPhoneEmail({ listing_id, leademail_primary_only }, isShowModal, typeModal) {
    return (dispatch, getState) => {
      const {
        teams: { currentTeam }
      } = getState();
      const teamUrl = currentTeam?.value ? "/team" : "";
      const userIdParams = currentTeam?.value ? { user_id: currentTeam.value } : {};

      dispatch(actions.setLoading("notifications", true));

      return apiClient({
        method: "post",
        responseType: "json",
        url: `/members/json-api${teamUrl}/listing/update-emails-phones`,
        data: qs.stringify({ listing_id, leademail_primary_only, ...userIdParams }),
        headers: {
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8"
        }
      })
        .then(() => {
          if (isShowModal) {
            dispatch({
              type: listing.SUBMIT_PHONE_EMAIL_SUCCESS,
              payload: typeModal
            });
          }
        })
        .catch(error => {
          dispatch({
            type: listing.SUBMIT_CHANGES_FAIL,
            payload: "Error saving changes"
          });
          throw error;
        })
        .finally(() => {
          dispatch(actions.setLoading("notifications", false));
        });
    };
  },

  removeVerifiedRecipient(type, { listingId, prkey, pathname }, isShowModal, typeModal) {
    return (dispatch, getState) => {
      const {
        teams: { currentTeam }
      } = getState();
      const teamUrl = currentTeam?.value ? "/team" : "";
      const userIdParams = currentTeam?.value ? { user_id: currentTeam.value } : {};
      const path = type === "email" ? "delete-listing-email" : "delete-listing-phone";

      dispatch(actions.setLoading("notifications", true));

      return apiClient({
        method: "post",
        responseType: "json",
        url: `/members/json-api${teamUrl}/listing/${path}`,
        data: { listing_id: listingId, prkey, ...userIdParams },
        headers: { "Content-Type": "application/json" }
      })
        .then(() => dispatch(actions.getMemberDataNew(listingId, pathname, null, false, { bustCache: true })))
        .then(member => {
          if (!member) {
            throw new Error("Error saving changes");
          }

          if (isShowModal) {
            dispatch({
              type: listing.SUBMIT_PHONE_EMAIL_SUCCESS,
              payload: typeModal
            });
          }
        })
        .catch(error => {
          dispatch({
            type: listing.SUBMIT_CHANGES_FAIL,
            payload: "Error saving changes"
          });
          throw error;
        })
        .finally(() => {
          dispatch(actions.setLoading("notifications", false));
        });
    };
  },

  submitImages(memberData, section, pathname, navigate) {
    return async (dispatch, getState) => {
      const {
        listing: { memberData: stateMemberData }
      } = getState();

      const { apiPrefix, userIdParams } = resolveListingTeamContext(getState, pathname);
      const isEvensOrJobsPage = isApiV2EditorPath(pathname);
      const listingName = resolveListingApiName(pathname);
      const url = isEvensOrJobsPage && listingName ? `${apiPrefix}/${listingName}?return_object=1` : "/members/json-api/listing/update";
      const autosaveSection = section || "photo";
      const autosaveListingId = stateMemberData?.listing_id || "";
      const autosaveOwnerId = userIdParams.user_id || "";
      const autosaveScope = `${apiPrefix}:${autosaveOwnerId}:${autosaveListingId}:${autosaveSection}`;
      const generation = await nextAutosaveSeq(autosaveScope);
      const orderingMeta = buildAutosaveOrderingMeta(autosaveSection, stateMemberData, generation);

      if (userIdParams.user_id) {
        memberData.append("user_id", userIdParams.user_id);
      }
      Object.entries(orderingMeta).forEach(([key, value]) => {
        memberData.append(key, value);
      });

      dispatch(actions.setLoading(section, true));

      return apiClient({
        method: "post",
        responseType: "json",
        url,
        data: memberData,
        headers: {
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8"
        }
      })
        .then(res => {
          if (res.headers["content-type"] === "text/html; charset=utf-8") {
            if (section != null) {
              dispatch(actions.setLoading(section, false));
            }
            navigate("/404");
          } else {
            const nextVersion = resolveAutosaveVersionFromResponse(res);
            if (section === "parent_company") {
              dispatch({
                type: listing.GET_MEMBER_DATA,
                payload: {
                  data: withUpdatedAutosaveVersion(
                    { ...stateMemberData, [section]: [res.data.data[section]], ...res.data.data.listing },
                    autosaveSection,
                    nextVersion
                  ),
                  successMessage: true
                }
              });
            } else {
              dispatch({
                type: listing.GET_MEMBER_DATA,
                payload: {
                  data: withUpdatedAutosaveVersion({ ...stateMemberData, ...(res.data.data || res.data) }, autosaveSection, nextVersion),
                  successMessage: true
                }
              });
            }

            dispatch(actions.setLoading(section, false));
          }
          return res;
        })
        .catch(err => {
          if (section != null) {
            dispatch(actions.setLoading(section, false));
          }
          if (err?.response?.status === 409 && err?.response?.data?.stale) {
            handleAutosaveConflict(
              dispatch,
              actions.getMemberDataNew(
                resolveDashboardListingId(stateMemberData?.listing_id, stateMemberData?.parent_id) || autosaveListingId,
                pathname,
                navigate,
                false,
                { bustCache: true, skipLoading: true }
              ),
              resolveStaleReasonFromResponse(err),
              section
            );
            return null;
          }
          if (err?.response?.data?.error === "Not found") {
            navigate("/404");
          } else {
            dispatch({
              type: listing.SUBMIT_CHANGES_FAIL,
              payload: resolveApiErrorMessage(err, "Error saving changes"),
              section
            });
          }
          return null;
        });
    };
  },

  submitChanges(memberData, section, pathname, isCloseModals, isReload, navigate) {
    return (dispatch, getState) => {
      const {
        listing: { memberData: storeMemberData }
      } = getState();
      const { apiPrefix, userIdParams } = resolveListingTeamContext(getState, pathname);
      const rawAutosaveListingId = storeMemberData?.listing_id || "";
      const autosaveListingId = resolveDashboardListingId(rawAutosaveListingId, storeMemberData?.parent_id) || rawAutosaveListingId;
      const autosaveOwnerId = userIdParams.user_id || "";
      const autosaveScope = `${apiPrefix}:${autosaveOwnerId}:${autosaveListingId}:${section}`;

      return trackAutosaveWork(
        (async () => {
          const userIdQuery = userIdParams.user_id ? `&user_id=${userIdParams.user_id}` : "";

          const isEvensOrJobsPage = isApiV2EditorPath(pathname);
          const listingName = resolveListingApiName(pathname);
          const saveCommand = typeof isCloseModals === "string" && isCloseModals.startsWith("save_") ? isCloseModals : null;
          const useSectionPost = Boolean(isEvensOrJobsPage && saveCommand);
          const url = listingName ? `${apiPrefix}/${listingName}?return_object=1${userIdQuery}` : "/members/json-api/listing/update";
          const generation = await nextAutosaveSeq(autosaveScope);
          const orderingMeta = buildAutosaveOrderingMeta(section, storeMemberData, generation);

          const newData = {
            ...storeMemberData,
            ...memberData
          };

          const submitRequest = () => {
            dispatch(actions.setLoading(section, true));

            return apiClient({
              method: useSectionPost ? "post" : isEvensOrJobsPage ? "put" : "post",
              responseType: "json",
              url,
              data: qs.stringify({
                ...newData,
                ...(saveCommand ? { command: saveCommand } : {}),
                ...(userIdParams.user_id ? { user_id: userIdParams.user_id } : {}),
                ...orderingMeta
              }),
              headers: {
                "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8"
              }
            });
          };

          // Same scoped queue as autoSaveListing so concurrent flushAll row saves
          // for one section keep only the latest payload and generation (SEC-007).
          return enqueueLatestSerializedAutosave(autosaveScope, submitRequest)
            .then(res => {
              const isLatestAutosave = autosaveGeneration.get(autosaveScope) === generation;

              if (!isLatestAutosave) {
                return res;
              }

              if (res.headers["content-type"] === "text/html; charset=utf-8") {
                if (section != null) {
                  dispatch(actions.setLoading(section, false));
                }
                navigate("/404");
              } else {
                const resData = res.data?.data || {};
                const listingPatch = resData.listing || {};
                const newListingId = listingPatch.listing_id ?? resData.listing_id;
                const listingIdChanged =
                  newListingId != null &&
                  storeMemberData?.listing_id != null &&
                  Number(newListingId) !== Number(storeMemberData.listing_id);
                const nextVersion = resolveAutosaveVersionFromResponse(res);

                if (isReload || listingIdChanged) {
                  if (listingIdChanged) {
                    dispatch({
                      type: listing.GET_MEMBER_DATA,
                      payload: {
                        data: withUpdatedAutosaveVersion(
                          {
                            ...storeMemberData,
                            ...memberData,
                            ...listingPatch,
                            ...(resData.licenses ? { licenses: resData.licenses } : {}),
                            ...(resData[section] !== undefined ? { [section]: resData[section] } : {})
                          },
                          section,
                          nextVersion
                        ),
                        successMessage: false
                      }
                    });
                  }
                  const refetchListingId = resolveDashboardListingId(newListingId || storeMemberData.listing_id, listingPatch.parent_id);
                  dispatch(
                    this.getMemberDataNew(refetchListingId, pathname, navigate, true, {
                      bustCache: Boolean(listingIdChanged)
                    })
                  );
                } else {
                  dispatch({
                    type: listing.GET_MEMBER_DATA,
                    payload: {
                      data: withUpdatedAutosaveVersion(
                        { ...storeMemberData, ...memberData, ...resData, ...listingPatch },
                        section,
                        nextVersion
                      ),
                      successMessage: true
                    }
                  });
                }
                if (isCloseModals === true) {
                  dispatch(this.closeAllModals());
                }
                if (section != null) {
                  dispatch(actions.setLoading(section, false));
                }
              }
              clearAutosavePersistFailure(autosaveScope);
              return res;
            })
            .catch(err => {
              const isLatestAutosave = autosaveGeneration.get(autosaveScope) === generation;

              if (isLatestAutosave && section != null) {
                dispatch(actions.setLoading(section, false));
              }
              if (!isLatestAutosave) {
                return null;
              }
              if (err?.response?.status === 409 && err?.response?.data?.stale) {
                const staleReason = resolveStaleReasonFromResponse(err);
                applyStaleAutosavePersistOutcome(autosaveScope, staleReason);
                handleAutosaveConflict(
                  dispatch,
                  actions.getMemberDataNew(
                    resolveDashboardListingId(storeMemberData?.listing_id, storeMemberData?.parent_id) || autosaveListingId,
                    pathname,
                    navigate,
                    false,
                    { bustCache: true, skipLoading: true }
                  ),
                  staleReason,
                  section
                );
                return null;
              }
              if (err?.response?.data?.error === "Not found") {
                noteAutosavePersistFailure(autosaveScope);
                navigate("/404");
              } else {
                noteAutosavePersistFailure(autosaveScope);
                dispatch({
                  type: listing.SUBMIT_CHANGES_FAIL,
                  payload: "Error saving changes",
                  section
                });
              }
              return null;
            });
        })(),
        autosaveScope
      );
    };
  },

  autoSaveListing(field, section, pathname, command, abortController, navigate) {
    return (dispatch, getState) => {
      const {
        listing: { memberData }
      } = getState();

      const { apiPrefix, userIdParams } = resolveListingTeamContext(getState, pathname);
      const isEvensOrJobsPage = isApiV2EditorPath(pathname);
      const listingName = resolveListingApiName(pathname);
      const url = isEvensOrJobsPage && listingName ? `${apiPrefix}/${listingName}` : "/members/json-api/listing/update";
      const rawAutosaveListingId = field?.listing_id || memberData?.listing_id || "";
      const autosaveListingId = resolveAutosaveDashboardListingId(rawAutosaveListingId, memberData);
      const autosaveOwnerId = userIdParams.user_id || "";
      const listingScope = `${apiPrefix}:${autosaveOwnerId}:${autosaveListingId}:`;
      const autosaveScope = `${listingScope}${section}`;

      // A save identical to the latest unsettled one in this scope shares that save's request.
      const signature = buildAutosaveSignature(field, command, abortController);
      const duplicate = findDuplicateAutosave(autosaveScope, signature);
      if (duplicate) {
        return duplicate;
      }
      const latestSave = trackLatestAutosave(autosaveScope, signature);

      beginAutosaveThunk(autosaveScope);
      dispatch(actions.setUnsavedSection(section, listingUnsavedSectionStatus.pending, listingScope));

      const call = trackAutosaveWork(
        (async () => {
          let generation;
          try {
            generation = await nextAutosaveSeq(autosaveScope);
          } catch {
            latestSave.settle();
            endAutosaveThunk(autosaveScope);
            if (!autosaveThunkInflight.has(autosaveScope)) {
              dispatch(actions.setUnsavedSection(section, listingUnsavedSectionStatus.failed, listingScope));
              noteAutosavePersistFailure(autosaveScope);
            }
            return null;
          }

          const newData = { ...field };
          const orderingMeta = buildAutosaveOrderingMeta(section, memberData, generation);
          const submitAutosave = () => {
            dispatch(actions.setLoading(section, true));

            return apiClient({
              method: "post",
              responseType: "json",
              url,
              data: qs.stringify({
                ...newData,
                ...userIdParams,
                command,
                ...orderingMeta
              }),
              signal: abortController?.signal,
              headers: {
                "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8"
              }
            });
          };

          const request = enqueueLatestSerializedAutosave(autosaveScope, submitAutosave);

          // Leave the in-flight count as soon as this save settles, not in `finally`: saves the
          // queue collapsed into one request settle in the same tick, and the latest one must
          // not still count its superseded siblings when it decides to clear the section.
          let hasLeftInflight = false;
          const leaveInflight = () => {
            if (!hasLeftInflight) {
              hasLeftInflight = true;
              latestSave.settle();
              endAutosaveThunk(autosaveScope);
            }
          };

          return request
            .then(res => {
              leaveInflight();

              if (autosaveGeneration.get(autosaveScope) !== generation) {
                return res;
              }

              const resData = res.data.data || {};
              const editorListingId = resData.listing?.listing_id || memberData.listing_id;
              const listingId = resolveDashboardListingId(editorListingId, resData.listing?.parent_id);
              const listingIdChanged =
                resData.listing?.listing_id != null && Number(resData.listing.listing_id) !== Number(memberData.listing_id);
              const listingPatch = resData.listing || {};
              const sectionPatch =
                section === "socials"
                  ? { socials: mergeSocialsAfterAutosave(newData.socials, resData.socials) }
                  : section === "languages"
                  ? { languages: mergeLanguagesAfterAutosave(newData.languages, resData.languages) }
                  : section === "insurances"
                  ? { insurances: mergeInsurancesAfterAutosave(newData.insurances, resData.insurances) }
                  : resData[section] !== undefined
                  ? { [section]: resData[section] }
                  : {};
              const scalarAutosavePatch = listingScalarAutosaveFields.reduce((acc, scalarField) => {
                if (resData[scalarField] !== undefined) {
                  acc[scalarField] = resData[scalarField];
                } else if (newData[scalarField] !== undefined) {
                  acc[scalarField] = newData[scalarField];
                }
                return acc;
              }, {});
              const nextVersion = resolveAutosaveVersionFromResponse(res);
              const mergedData = withUpdatedAutosaveVersion(
                {
                  ...memberData,
                  ...sectionPatch,
                  ...listingPatch,
                  ...scalarAutosavePatch
                },
                section,
                nextVersion
              );

              // directories_availability: API returns a partial payload; refetch full member data with loading overlay.
              // listing_id change (first edit of approved parent): merge the API patch immediately, then sync in the
              // background without preloaders.memberData so blur autosave does not unmount the details form.
              // socials/languages/insurances: merge sent rows + partial API rows (see merge*AfterAutosave helpers).
              if (section === "directories_availability" && listingId) {
                dispatch({
                  type: listing.GET_MEMBER_DATA,
                  payload: { data: mergedData, successMessage: true }
                });
                dispatch(actions.getMemberDataNew(listingId, pathname, navigate, true, { bustCache: true }));
              } else if (listingIdChanged && listingId) {
                dispatch({
                  type: listing.GET_MEMBER_DATA,
                  payload: { data: mergedData, successMessage: true }
                });
                dispatch(
                  actions.getMemberDataNew(listingId, pathname, navigate, false, {
                    bustCache: true,
                    skipLoading: true
                  })
                );
              } else {
                dispatch({
                  type: listing.GET_MEMBER_DATA,
                  payload: { data: mergedData, successMessage: true }
                });
              }
              dispatch(actions.setLoading(section, false));
              if (!autosaveThunkInflight.has(autosaveScope)) {
                dispatch(actions.setUnsavedSection(section, null, listingScope));
              }
              clearAutosavePersistFailure(autosaveScope);
              return res;
            })
            .catch(err => {
              leaveInflight();

              const isLatestAutosave = autosaveGeneration.get(autosaveScope) === generation;
              const isCancel = apiClient.isCancel(err);
              const isStale = err?.response?.status === 409 && Boolean(err?.response?.data?.stale);
              const staleReason = isStale ? resolveStaleReasonFromResponse(err) : null;
              // Superseded: the server already holds a newer save from this client and the
              // refetch below reconciles the editor, so nothing is left unsaved (same rule as
              // the publish gate in applyStaleAutosavePersistOutcome).
              const isReconciledByRefetch = staleReason === "superseded";

              // Latest-in-flight only: a superseded request must not clear the spinner
              // while a newer save is still running. Cancel of the latest save must.
              if (isLatestAutosave) {
                dispatch(actions.setLoading(section, false));
                dispatch(
                  actions.setUnsavedSection(
                    section,
                    isCancel || isReconciledByRefetch ? null : listingUnsavedSectionStatus.failed,
                    listingScope
                  )
                );
              }

              if (isCancel || !isLatestAutosave) {
                return null;
              }

              if (isStale) {
                applyStaleAutosavePersistOutcome(autosaveScope, staleReason);
                handleAutosaveConflict(
                  dispatch,
                  actions.getMemberDataNew(
                    resolveDashboardListingId(memberData?.listing_id, memberData?.parent_id) || autosaveListingId,
                    pathname,
                    navigate,
                    false,
                    { bustCache: true, skipLoading: true }
                  ),
                  staleReason,
                  section
                );
                return null;
              }
              if (err?.response?.data?.error === "Not found") {
                noteAutosavePersistFailure(autosaveScope);
                navigate("/404");
              } else {
                noteAutosavePersistFailure(autosaveScope);
                dispatch({
                  type: listing.SUBMIT_CHANGES_FAIL,
                  payload: "Error saving changes",
                  section
                });
                const onRefused = abortController?.onRefused;
                const refusal = typeof onRefused === "function" ? resolveAutosaveRefusal(err) : "";
                if (refusal) {
                  onRefused(refusal);
                }
              }
              return null;
            })
            .finally(() => {
              leaveInflight();
            });
        })(),
        autosaveScope
      );

      // Backstop: the save above settles its record on the exits it knows about. If the code between
      // the sequence number and the request throws, the record would stay and a retry of the same
      // payload would share this dead promise. Settling again is harmless: a newer record is kept.
      call.then(latestSave.settle, latestSave.settle);

      return latestSave.register(call);
    };
  },

  clearSuccess() {
    return {
      type: listing.CLEAR_CHANGES_FIELD
    };
  },

  setMembershipClear() {
    return {
      type: listing.SET_MEMBERSHIP_DATA_CLEAR
    };
  },

  toggleActivityOfModal(modal, isActive) {
    return {
      type: listing.TOGGLE_ACTIVITY_OF_MODAL,
      payload: {
        modal,
        isActive
      }
    };
  },

  activeNotificationModal(modal, data) {
    return {
      type: listing.ACTIVE_NOTIFICATION_MODAL,
      payload: {
        modal,
        data
      }
    };
  },

  clearAllModals() {
    return {
      type: listing.CLEAR_ALL_MODALS
    };
  },

  deletePrimaryImage(listingId, pathname) {
    return (dispatch, getState) => {
      const {
        teams: { currentTeam }
      } = getState();
      const teamUrl = currentTeam?.value ? "-team" : "";
      const userIdParams = currentTeam?.value ? `&user_id=${currentTeam.value}` : "";

      dispatch(actions.setLoading("photo", true));

      const listingName = resolveListingApiName(pathname);

      return apiClient({
        method: "delete",
        responseType: "json",
        url: `/members/api-v2${teamUrl}/${listingName}?command=primary_image&listing_id=${listingId}${userIdParams}`
      })
        .then(() => {
          dispatch({
            type: listing.DELETE_PRIMARY_IMAGE
          });
          return true;
        })
        .catch(error => {
          dispatch({
            type: listing.DELETE_PRIMARY_IMAGE_FAIL,
            payload: resolveApiErrorMessage(error, "Failed to delete image")
          });
        })
        .finally(() => {
          dispatch(actions.setLoading("photo", false));
        });
    };
  },

  deleteAdditionalImages(listingId, imageId, pathname) {
    return (dispatch, getState) => {
      const {
        teams: { currentTeam }
      } = getState();
      const teamUrl = currentTeam?.value ? "-team" : "";
      const userIdParams = currentTeam?.value ? `&user_id=${currentTeam.value}` : "";

      dispatch(actions.setLoading("photo", true));

      const listingName = resolveListingApiName(pathname);

      return apiClient({
        method: "delete",
        responseType: "json",
        url: `/members/api-v2${teamUrl}/${listingName}?command=additional_image&listing_id=${listingId}&image_id=${imageId}${userIdParams}`
      })
        .then(() => {
          dispatch({
            type: listing.DELETE_ADDITIONAL_IMAGE,
            payload: imageId
          });
          // The Listing Editor v2 Gallery block reads the result as saved or failed (GROWTH-7806), as deletePrimaryImage does.
          return true;
        })
        .catch(error => {
          dispatch({
            type: listing.DELETE_IMAGE_FAIL,
            payload: error
          });
          return null;
        });
    };
  },

  cleanUpgradeOptions() {
    return {
      type: listing.UPGRADE_CLEAN
    };
  },

  deleteCoverImage(listingId, imageId, pathname, section) {
    return (dispatch, getState) => {
      const {
        teams: { currentTeam }
      } = getState();
      const teamUrl = currentTeam?.value ? "-team" : "";
      const userIdParams = currentTeam?.value ? `&user_id=${currentTeam.value}` : "";

      dispatch(actions.setLoading("photo", true));
      const imageCommand = section ? "parent_company_image" : "cover_image";
      const listingName = resolveListingApiName(pathname);
      const url = `/members/api-v2${teamUrl}/${listingName}?listing_id=${listingId}&command=${imageCommand}&image_id=${imageId}${userIdParams}`;
      return apiClient({
        method: "delete",
        responseType: "json",
        url
      })
        .then(() => {
          if (section === "parent") {
            dispatch({
              type: listing.DELETE_PARENT_COVER_IMAGE,
              payload: listingId
            });
          } else {
            dispatch({
              type: listing.DELETE_COVER_IMAGE,
              payload: listingId
            });
          }
          return true;
        })
        .catch(error => {
          dispatch({
            type: listing.DELETE_COVER_IMAGE_FAIL,
            payload: resolveApiErrorMessage(error, "Failed to delete image")
          });
        })
        .finally(() => {
          dispatch(actions.setLoading("photo", false));
        });
    };
  },

  // planModal: the plan modal (upgrade, renew or payPending) the member is leaving, so Payment's Back and Confirm's Edit
  // can reopen it (GROWTH-7483). The Pro Bono flow passes none.
  setSavedPaymentData(planModal) {
    return {
      type: listing.SET_SAVED_PAYMENT_DATA,
      payload: planModal
    };
  },

  clearSavedPaymentData() {
    return {
      type: listing.CLEAR_SAVED_PAYMENT_DATA
    };
  },

  deleteMembership(memberData, isHomePage, pathname, isListingDetail, navigate) {
    return (dispatch, getState) => {
      const listingId = memberData.listing_id;
      const { listingName, ...requestConfig } = buildDeleteMembershipRequest(getState, {
        pathname,
        listingId
      });

      dispatch(actions.setLoading("memberData", true));
      dispatch(actions.setLoading("deleteMembership", true));

      apiClient({
        responseType: "json",
        ...requestConfig
      })
        .then(() => {
          if (isHomePage) {
            dispatch(homeActions.getDashboardInfo());
          }

          dispatch({
            type: listing.DELETE_SUCCESS,
            payload: listingId
          });

          if (isListingDetail && typeof navigate === "function") {
            navigate(listingProfessionType[listingName] || "/listings");
          }
        })
        .catch(error => {
          console.error("deleteMembership", error);
          dispatch(actions.setLoading("memberData", false));
          dispatch(actions.setLoading("deleteMembership", false));
          dispatch({
            type: listing.DELETE_FAIL,
            payload: "Failed to delete listing"
          });
        });
    };
  },

  // `listPathname` (GROWTH-7312): the page's own path, for the list reload. `pathname` stays what routes the request: the Listings
  // page passes the row's type title there ("organizations"), which says nothing about which list the page shows.
  downgradeMembership(id, isSingleListing, isHomePage, pathname, navigate, listPathname) {
    return (dispatch, getState) => {
      dispatch(actions.setLoading("downgrade", true));

      const request = buildListingCommandRequest(getState, {
        pathname,
        listingId: id,
        command: "cancel",
        legacyJsonApiPath: "listing/cancel"
      });

      apiClient({
        responseType: "json",
        ...request
      })
        .then(res => {
          dispatch({
            type: listing.DOWNGRADE_SUCCESS,
            payload: {
              id,
              canceledDate: res?.data?.data?._canceled_to_date,
              unCancelIds: res?.data?.data?.uncancel_ids
            }
          });

          if (isHomePage) {
            dispatch(homeActions.getDashboardInfo());
          }

          if (isSingleListing) {
            dispatch(this.getMemberDataNew(id, pathname, navigate));
          } else {
            dispatch(this.getExistingMembershipsNew({}, true, 1, [], [], [], "", "", 10, [], [], "", listPathname || pathname));
          }
        })
        .catch(error => {
          dispatch({
            type: listing.DOWNGRADE_FAIL,
            payload: resolveApiErrorMessage(error, "Failed to cancel membership")
          });
        })
        .finally(() => {
          dispatch(actions.setLoading("downgrade", false));
        });
    };
  },

  downgradeMembershipHow(id, isSingleListing, isHomePage, pathname, navigate) {
    return (dispatch, getState) => {
      dispatch(actions.setLoading("downgrade", true));

      const request = buildListingCommandRequest(getState, {
        pathname,
        listingId: id,
        command: "cancel_now",
        legacyJsonApiPath: "listing/cancel-now"
      });

      apiClient({
        responseType: "json",
        ...request
      })
        .then(res => {
          dispatch({
            type: listing.DOWNGRADE_SUCCESS,
            payload: { id, canceledDate: res?.data?.data?._canceled_to_date }
          });

          if (isHomePage) {
            dispatch(homeActions.getDashboardInfo());
          } else if (isSingleListing) {
            dispatch(this.getMemberDataNew(id, pathname, navigate));
          } else {
            dispatch(this.getExistingMembershipsNew({}, true, 1, [], [], [], "", "", 10, [], [], "", pathname));
          }
        })
        .catch(error => {
          dispatch({
            type: listing.DOWNGRADE_FAIL,
            payload: resolveApiErrorMessage(error, "Failed to cancel membership")
          });
        })
        .finally(() => {
          dispatch(actions.setLoading("downgrade", false));
        });
    };
  },

  getDirectoriesProfessions() {
    return dispatch => {
      apiClient({
        method: "get",
        responseType: "json",
        url: "/members/json-api/index/lists"
      })
        .then(result => {
          const { directories, professions, heritages } = result.data;

          dispatch({
            type: listing.GET_DIRECTORIES_PROFESSIONS,
            payload: {
              directories,
              professions,
              heritages
            }
          });
        })
        .catch(error => {
          dispatch({
            type: listing.GET_DIRECTORIES_PROFESSIONS_FAIL,
            payload: error
          });
        });
    };
  },

  unDowngradeMembership(id, isSingleListing, isHomePage, pathname, navigate) {
    return (dispatch, getState) => {
      dispatch(actions.setLoading("downgrade", true));

      const request = buildListingCommandRequest(getState, {
        pathname,
        listingId: id,
        command: "uncancel",
        legacyJsonApiPath: "listing/uncancel"
      });

      apiClient({
        responseType: "json",
        ...request
      })
        .then(() => {
          dispatch({
            type: listing.UN_DOWNGRADE_SUCCESS
          });

          if (isHomePage) {
            dispatch(homeActions.getDashboardInfo());
            // GROWTH-7414: the store's rows still hold the cancel date and undo ids this undo just reversed.
            dispatch(this.getExistingMembershipsNew({}, true, 1, [], [], [], "", "", 10, [], [], "", pathname));
          } else if (isSingleListing) {
            dispatch(this.getMemberDataNew(id, pathname, navigate));
          } else {
            dispatch(this.getExistingMembershipsNew({}, true, 1, [], [], [], "", "", 10, [], [], "", pathname));
          }
        })
        .catch(err => {
          dispatch({
            type: listing.UN_DOWNGRADE_FAIL,
            payload: resolveApiErrorMessage(err, "Failed to cancel the downgrade")
          });
        });
    };
  },

  getAllListings(
    filter,
    isLoading,
    page,
    dirId = [],
    professionGroupId = [],
    freeSponsor = [],
    professionName = "",
    sort,
    perPage,
    listActiveChange,
    listActiveStatus,
    isDraft,
    pathname,
    config,
    userId
  ) {
    return (dispatch, getState) => {
      const {
        teams: { currentTeam }
      } = getState();
      const ownerId = currentTeam?.value || userId;
      const teamUrl = ownerId ? "-team" : "";
      const userIdParams = ownerId ? { user_id: ownerId } : {};
      const isEvensOrJobsPage = ["/my-events", "/my-jobs", "/my-organizations"].includes(pathname);
      const isOrganizations = ["/my-organizations"].includes(pathname);
      const listingName = isOrganizations ? "business" : pathname?.replace("/my-", "").toLowerCase();
      const professionId = isEvensOrJobsPage ? { profession_id: config[`${listingName}Profession`] } : {};

      if (!isLoading) {
        dispatch(actions.setLoading("memberships", true));
      }

      // GROWTH-7659: a name with only spaces is no search; surrounding spaces are not part of the name.
      const trimmedName = professionName?.trim();
      const searchName = trimmedName ? { prof_name: trimmedName } : {};
      const sortSearch = sort ? { sort } : {};
      const perPageSearch = perPage ? { limit: perPage } : {};
      const listActiveChangeSearch = listActiveChange?.length ? { change_status: listActiveChange } : {};
      const listActiveStatusSearch = listActiveStatus?.length ? { publish_status: listActiveStatus } : {};
      const isDraftParams = isDraft ? { is_draft: "1" } : {};

      const filterParams = queryString.stringify(
        {
          dir_id: dirId,
          profession_group_id: professionGroupId,
          freesponsor: freeSponsor,
          page,
          ...searchName,
          ...sortSearch,
          ...perPageSearch,
          ...listActiveChangeSearch,
          ...listActiveStatusSearch,
          ...isDraftParams,
          ...userIdParams,
          ...professionId,
          expand: "parent_company"
        },
        { arrayFormat: "index" }
      );

      return apiClient({
        method: "get",
        responseType: "json",
        url: `/members/api-v2${teamUrl}/listings?${filterParams}`
      })
        .then(res => {
          const data = res.data?.data ?? res.data;
          dispatch({
            type: listing.GET_MEMBERSHIPS,
            payload: {
              listings: data?.records ?? [],
              count: data?.counting ?? {},
              // What this list was loaded with, so a reload after an action can ask for the same page, sort and filters (GROWTH-7312).
              query: {
                page,
                dirId,
                professionGroupId,
                freeSponsor,
                professionName,
                sort,
                perPage,
                listActiveChange,
                listActiveStatus,
                isDraft,
                pathname,
                userId
              },
              filters: { ...listFilterOptions, directories: res.data?.filters?.directories ?? [] },
              lastPage: res.data?.pagination?.total_pages || 0,
              currentPage: res.data?.pagination?.current_page || 0,
              perPage: res.data?.pagination?.records_per_page || 0,
              isEvensOrJobsPage
            }
          });
        })
        .catch(error => {
          dispatch({
            type: listing.GET_MEMBERSHIPS_FAIL,
            payload: error.response
          });
        })
        .finally(() => {
          if (!isLoading) {
            dispatch(actions.setLoading("memberships", false));
          }
        });
    };
  },

  getAllListingsIncludeProfileListings(page, search = "", limit) {
    return (dispatch, getState) => {
      const {
        teams: { currentTeam }
      } = getState();
      const teamUrl = currentTeam?.value ? "-team" : "";
      const userIdParams = currentTeam?.value ? { user_id: currentTeam.value } : {};

      // GROWTH-7659: a name with only spaces is no search; surrounding spaces are not part of the name.
      const trimmedSearch = search?.trim();
      const searchName = trimmedSearch ? { prof_name: trimmedSearch } : {};

      const filterParams = queryString.stringify(
        {
          page,
          limit,
          ...searchName,
          ...userIdParams,
          ...{ include_profile_listings: 1 },
          expand: "parent_company"
        },
        { arrayFormat: "index" }
      );

      return apiClient({
        method: "get",
        responseType: "json",
        url: `/members/api-v2${teamUrl}/listings?${filterParams}`
      })
        .then(res => {
          dispatch({
            type: listing.GET_MEMBERSHIPS,
            payload: {
              listings: res.data.data.records,
              lastPage: res.data.pagination.total_pages || 0,
              currentPage: res.data.pagination.current_page || 0,
              perPage: res.data.pagination.records_per_page || 0,
              isEvensOrJobsPage: true
            }
          });
        })
        .catch(error => {
          dispatch({
            type: listing.GET_MEMBERSHIPS_FAIL,
            payload: error.response
          });
        });
    };
  },

  getExistingMembershipsNew(
    filter,
    isLoading,
    page,
    dirId = [],
    professionGroupId = [],
    freeSponsor = [],
    professionName = "",
    sort,
    perPage,
    listActiveChange,
    listActiveStatus,
    isDraft,
    pathname,
    userId,
    // GROWTH-7996: dashboard Listing IDs the search typed (the listing's own id or its parent's); [] for a name search.
    listingIds = [],
    // GROWTH-8002: Event Listings v2 only: the When select ("upcoming", "past" or "all") and whether Past events are kept out ("0") or
    // the only ones ("1"). GROWTH-8008: Job Listings v2 only: { closed, applications }, each "0" or "1".
    eventFilters = {}
  ) {
    return (dispatch, getState) => {
      const {
        teams: { currentTeam },
        listing: listingState
      } = getState();

      // GROWTH-7312: which loader reloads the list for the page the action ran on.
      // - "/listings" loads through getAllListings (a different URL): ask for what it last loaded, the first page with no filters
      //   when it has loaded nothing yet.
      // - "/my-events", "/my-jobs", "/my-organizations", and the bare type title the Listings modals pass for their own request
      //   ("jobs"): requested below, as before.
      // - any other page path ("/pro-bono", "/listings/123") has no list to reload: no request, instead of "api-v2//<path>".
      if (!pathname) {
        return Promise.resolve();
      }

      if (pathname === "/listings") {
        const query = listingState?.membershipsQuery || {
          page,
          dirId,
          professionGroupId,
          freeSponsor,
          professionName,
          sort,
          perPage,
          listActiveChange,
          listActiveStatus,
          isDraft,
          pathname,
          userId
        };

        return dispatch(
          this.getAllListings(
            filter,
            isLoading,
            query.page,
            query.dirId,
            query.professionGroupId,
            query.freeSponsor,
            query.professionName,
            query.sort,
            query.perPage,
            query.listActiveChange,
            query.listActiveStatus,
            query.isDraft,
            query.pathname,
            listingState?.listingConfig,
            query.userId
          )
        );
      }

      const isEvensOrJobsPage = ["/my-events", "/my-jobs", "/my-organizations"].includes(pathname);

      if (pathname.startsWith("/") && !isEvensOrJobsPage) {
        return Promise.resolve();
      }

      const ownerId = currentTeam?.value || userId;
      const teamUrl = ownerId ? "-team" : "";
      const userIdParams = ownerId ? { user_id: ownerId } : {};
      const listingName = pathname.replace("/my-", "").toLowerCase();

      const sortSearch = sort ? { sort } : {};
      const perPageSearch = perPage ? { limit: perPage } : {};
      const listActiveChangeSearch = listActiveChange?.length ? { change_status: listActiveChange } : {};
      const listActiveStatusSearch = listActiveStatus?.length ? { publish_status: listActiveStatus } : {};
      const isDraftParams = isDraft ? { is_draft: "1" } : {};
      const isJobPage = pathname.includes("/my-jobs");

      const object = {
        dir_id: dirId,
        profession_group_id: professionGroupId,
        sponsorship: freeSponsor,
        dashboard_id: listingIds,
        event_when: eventFilters.when === "upcoming" || eventFilters.when === "past" ? eventFilters.when : "",
        event_ended: eventFilters.ended || "",
        // GROWTH-8008: Job Listings v2: Closed (the Status choice) and the Applications select, "1" closed / "0" open.
        job_closed: eventFilters.closed || "",
        job_applications: eventFilters.applications || "",
        ...listActiveChangeSearch,
        ...listActiveStatusSearch,
        ...isDraftParams
      };

      const keys = object ? Object.keys(object).filter(item => object[item]?.length) : {};

      const newQueryObject = keys.reduce((acc, value, index) => {
        acc[`filters[${index}][field]`] = value;
        acc[`filters[${index}][value]`] = object[value];

        return acc;
      }, {});

      // GROWTH-7996: "name" is the Organization Listings page's Name A-Z (the organizations endpoint sorts it); desc is the default.
      // Field and direction are two keys: one key holding "=" and "&" is encoded whole, and the server then applies no sort.
      const sorting =
        sortSearch.sort === "name"
          ? { "sorters[0][field]": "name", "sorters[0][dir]": "asc" }
          : sortSearch.sort === "event"
          ? { "sorters[0][field]": "event_date", "sorters[0][dir]": "asc" }
          : sortSearch.sort === "deadline"
          ? { "sorters[0][field]": "deadline", "sorters[0][dir]": "asc" }
          : sortSearch.sort && sortSearch.sort !== "desc"
          ? { "sorters[0][field]": "updated", "sorters[0][dir]": sortSearch.sort }
          : {};
      const pageParams = page ? { page } : {};
      const jobListingsParams = isJobPage ? { expand: "parent_company" } : {};
      // GROWTH-7659: a name with only spaces is no search; surrounding spaces are not part of the name.
      const trimmedName = professionName?.trim();
      const profNameParam = trimmedName ? { prof_name: trimmedName } : {};
      const newQueryString = queryString.stringify(
        { ...newQueryObject, ...profNameParam, ...sorting, ...pageParams, ...perPageSearch, ...userIdParams, ...jobListingsParams },
        { arrayFormat: "bracket" }
      );

      const requestUrl = `/members/api-v2${teamUrl}/${listingName}${newQueryString ? `?${newQueryString}` : ""}`;
      const requestKey = `memberships:${requestUrl}`;

      if (pendingMembershipRequests.has(requestKey)) {
        return pendingMembershipRequests.get(requestKey);
      }

      if (!isLoading) {
        dispatch(actions.setLoading("memberships", true));
      }

      const request = apiClient({
        method: "get",
        responseType: "json",
        url: requestUrl
      })
        .then(res => {
          const responseData = res.data?.data;
          const listings = Array.isArray(responseData) ? responseData : responseData?.records ?? [];
          const pagination = res.data?.pagination ?? {};

          dispatch({
            type: listing.GET_MEMBERSHIPS,
            payload: {
              listings,
              count: responseData?.counting,
              filters: isEvensOrJobsPage ? { ...listFilterOptions, directories: res.data?.filters?.directories ?? [] } : res.data?.filters,
              lastPage: pagination.total_pages || 0,
              currentPage: pagination.current_page || 0,
              perPage: pagination.records_per_page || 0,
              isEvensOrJobsPage
            }
          });
        })
        .catch(error => {
          dispatch({
            type: listing.GET_MEMBERSHIPS_FAIL,
            payload: error.response
          });
        })
        .finally(() => {
          dispatch(actions.setLoading("memberships", false));
          pendingMembershipRequests.delete(requestKey);
        });

      pendingMembershipRequests.set(requestKey, request);
      return request;
    };
  },

  getUpgradeOptions(id, coupon) {
    return (dispatch, getState) => {
      if (!hasListingId(id)) {
        return rejectMissingListingId(dispatch);
      }

      dispatch(actions.setLoading("upgradeOptions", true));

      dispatch({
        type: listing.GET_UPGRADE_OPTIONS_HIDE_ERROR
      });

      const params = {
        id,
        coupon
      };

      return apiClient(buildFinancialJsonApiGet(getState, "index/upgrade-options", params))
        .then(res => {
          if (res.data?.length) {
            dispatch({
              type: listing.GET_UPGRADE_OPTIONS,
              payload: res.data
            });
          } else {
            dispatch({
              type: listing.GET_UPGRADE_OPTIONS_SHOW_ERROR
            });
          }
        })
        .catch(error => {
          dispatch({
            type: listing.GET_UPGRADE_OPTIONS_FAIL,
            payload: resolveApiErrorMessage(error, i18n.t("dashboard_upgrade_error_load_options"))
          });
        })
        .finally(() => {
          dispatch(actions.setLoading("upgradeOptions", false));
        });
    };
  },

  getRenewOptions(id, coupon) {
    return (dispatch, getState) => {
      if (!hasListingId(id)) {
        return rejectMissingListingId(dispatch);
      }

      dispatch(actions.setLoading("upgradeOptions", true));

      dispatch({
        type: listing.GET_UPGRADE_OPTIONS_HIDE_ERROR
      });

      const params = {
        id,
        coupon
      };

      return apiClient(buildFinancialJsonApiGet(getState, "index/renew-options", params))
        .then(res => {
          dispatch({
            type: listing.GET_UPGRADE_OPTIONS,
            payload: res.data
          });
        })
        .catch(error => {
          dispatch({
            type: listing.GET_UPGRADE_OPTIONS_FAIL,
            payload: resolveApiErrorMessage(error, i18n.t("dashboard_upgrade_error_load_renewal"))
          });
        })
        .finally(() => {
          dispatch(actions.setLoading("upgradeOptions", false));
        });
    };
  },

  getPendingPayment(listing_id) {
    return (dispatch, getState) => {
      if (!hasListingId(listing_id)) {
        return rejectMissingListingId(dispatch);
      }

      dispatch(actions.setLoading("upgradeOptions", true));

      dispatch({
        type: listing.GET_UPGRADE_OPTIONS_HIDE_ERROR
      });

      return apiClient(buildFinancialJsonApiGet(getState, "listing/amount", { listing_id }))
        .then(res => {
          dispatch({
            type: listing.GET_UPGRADE_OPTIONS,
            payload: [res.data]
          });
        })
        .catch(error => {
          dispatch({
            type: listing.GET_UPGRADE_OPTIONS_FAIL,
            payload: resolveApiErrorMessage(error, i18n.t("dashboard_upgrade_error_load_payment"))
          });
        })
        .finally(() => {
          dispatch(actions.setLoading("upgradeOptions", false));
        });
    };
  },

  pauseListing(listingId, isHomePage, pathname, isListingDetail, isShowSuccess = true, navigate, listPathname) {
    return (dispatch, getState) => {
      dispatch(actions.setLoading("pause", true));

      const request = buildListingCommandRequest(getState, {
        pathname,
        listingId,
        command: "pause",
        legacyJsonApiPath: "listing/pause"
      });

      apiClient({
        responseType: "json",
        ...request
      })
        .then(() => {
          dispatch({
            type: listing.PAUSE_SUCCESS,
            payload: isShowSuccess
          });

          if (isHomePage) {
            dispatch(homeActions.getDashboardInfo());
          }

          if (isListingDetail) {
            dispatch(this.getMemberDataNew(listingId, pathname, navigate));
          } else {
            dispatch(this.getExistingMembershipsNew({}, true, 1, [], [], [], "", "", 10, [], [], "", listPathname || pathname));
          }
        })
        .catch(error => {
          dispatch({
            type: listing.PAUSE_FAIL,
            payload: error?.response?.data?.error || "Failed to pause membership"
          });
        })
        .finally(() => {
          dispatch(actions.setLoading("pause", false));
        });
    };
  },

  unPauseListing(listingId, isListingDetail, isHomePage, pathname, isShowSuccess = true, navigate, listPathname) {
    return (dispatch, getState) => {
      dispatch(actions.setLoading("unpause", true));

      const request = buildListingCommandRequest(getState, {
        pathname,
        listingId,
        command: "unpause",
        legacyJsonApiPath: "listing/unpause"
      });

      apiClient({
        responseType: "json",
        ...request
      })
        .then(() => {
          if (isHomePage) {
            dispatch(homeActions.getDashboardInfo());
          }

          dispatch({
            type: listing.UNPAUSE_SUCCESS,
            payload: isShowSuccess
          });

          if (!isHomePage) {
            if (isListingDetail) {
              dispatch(this.getMemberDataNew(listingId, pathname, navigate));
            } else {
              dispatch(this.getExistingMembershipsNew({}, true, 1, [], [], [], "", "", 10, [], [], "", listPathname || pathname));
            }
          }
        })
        .catch(error => {
          dispatch({
            type: listing.UNPAUSE_FAIL,
            payload: error?.response?.data?.error || "Failed to unpause membership"
          });
        })
        .finally(() => {
          dispatch(actions.setLoading("unpause", false));
        });
    };
  },

  publishListing(listingId, isReload, pathname, navigate) {
    return (dispatch, getState) => {
      const {
        listing: { memberData },
        account: { profile },
        teams: { currentTeam }
      } = getState();
      const apiV2TeamSuffix = currentTeam?.value ? "-team" : "";
      const jsonApiTeamPrefix = currentTeam?.value ? "/team" : "";
      const userIdParams = currentTeam?.value ? { user_id: currentTeam.value } : {};

      const isProfile = pathname.includes("/profile");
      const listingName = resolveListingApiName(pathname);

      const url = listingName
        ? `/members/api-v2${apiV2TeamSuffix}/${listingName}`
        : `/members/json-api${jsonApiTeamPrefix}/listing/publish`;

      dispatch(actions.setLoading("publishing", true));

      const scopePrefix = buildAutosaveListingScopePrefix(getState, pathname, listingId);

      return awaitAutosaveQuiescence({ scopePrefix })
        .then(quiescence => {
          if (!quiescence.ok) {
            dispatch({
              type: listing.SUBMIT_CHANGES_FAIL,
              payload: getAutosaveQuiescenceMessage(quiescence.reason)
            });
            return null;
          }

          return apiClient({
            method: listingName ? "put" : "post",
            responseType: "json",
            url,
            data: { listing_id: listingId, command: "publish", ...userIdParams },
            headers: {
              "Content-Type": "application/json"
            }
          });
        })
        .then(res => {
          if (!res) {
            return null;
          }
          const maxDateTracking = 60;
          if (
            memberData?.added &&
            differenceInCalendarDays(new Date(), new Date(memberData.added)) <= maxDateTracking &&
            !memberData?.is_premium
          ) {
            // Meta Pixel advanced matching (em/fn/ln) for retargeting — approved GROWTH-6320 SEC-02 (Nima, 2026-08-28).
            track(eventName.startTrial, { value: "12.00", currency: "USD", em: profile.email, fn: profile.name_f, ln: profile.name_l });
          }
          const responseData = res.data?.data || {};
          const hasParent = Number(responseData.parent_id ?? memberData?.parent_id) > 0;
          const submittedForReviewPatch = {
            ...responseData,
            is_draft: false,
            edit_status: hasParent ? editStatuses.pending : null,
            change_status: listingStatuses.pending
          };
          const applySubmittedForReviewState = () =>
            dispatch({
              type: listing.PUBLISH_LISTING,
              payload: submittedForReviewPatch
            });

          applySubmittedForReviewState();

          if (!isReload) {
            const pathSegments = pathname.split("/").filter(Boolean);
            const urlParamId = !isProfile && pathSegments[1] ? pathSegments[1] : null;
            const reloadId =
              urlParamId || resolveDashboardListingId(responseData.listing_id || listingId, responseData.parent_id) || listingId;
            return dispatch(this.getMemberDataNew(reloadId, pathname, navigate, false, { forceFullExpand: true, skipLoading: true })).then(
              result => {
                applySubmittedForReviewState();
                return result;
              }
            );
          }
          return null;
        })
        .catch(error => {
          dispatch({
            type: listing.PUBLISH_LISTING_FAIL,
            payload: getPublishErrorMessage(error)
          });
        })
        .finally(() => {
          dispatch(actions.setLoading("publishing", false));
        });
    };
  },

  handleNotificationEmail(email) {
    return (dispatch, getState) => {
      dispatch(this.setLoadingNotifications(true));

      const listingId = getState().listing?.memberData?.listing_id;

      // Returned so the modal can await the send and only then advance to the code step.
      return apiClient({
        method: "post",
        responseType: "json",
        url: "members/api-v2/verification/email/send-code",
        headers: { "Content-Type": "application/json" },
        data: {
          email,
          listing_id: listingId
        }
      })
        .then(({ data }) => {
          dispatch({
            type: listing.NOTIFICATION_EMAIL,
            payload: { email, token: data?.data?.token || "" }
          });
          return true;
        })
        .catch(error => {
          dispatch({
            type: listing.NOTIFICATION_EMAIL_FAIL,
            payload: error?.response?.data?.data?.error || error?.response?.data?.message || "Failed to update email"
          });
          return false;
        });
    };
  },

  handleVerifyEmail(code, pathname, email) {
    return (dispatch, getState) => {
      const {
        listing: { memberData, notificationEmail }
      } = getState();

      dispatch(this.setLoadingNotifications(true));

      return apiClient({
        method: "post",
        responseType: "json",
        url: "members/api-v2/verification/listing/email/confirm-code",
        headers: { "Content-Type": "application/json" },
        data: {
          code,
          token: notificationEmail.token,
          email: email || notificationEmail.email,
          listing_id: memberData.listing_id
        }
      })
        .then(() =>
          // confirm-code already INSERTs the row (VerificationService::addVerifiedEmailToListing).
          // Re-posting the whole list via update-emails-phones is what destroys recipients added elsewhere.
          dispatch(actions.getMemberDataNew(memberData.listing_id, pathname, null, false, { bustCache: true }))
        )
        .then(member => {
          if (!member) {
            dispatch({
              type: listing.NOTIFICATION_VERIFICATION_EMAIL_FAIL,
              payload: { saveError: "Error saving changes" }
            });
            return;
          }

          dispatch({
            type: listing.NOTIFICATION_VERIFICATION_EMAIL_SUCCESS
          });
        })
        .catch(error => {
          dispatch({
            type: listing.NOTIFICATION_VERIFICATION_EMAIL_FAIL,
            payload: listingConfirmCodeFailPayload(error, LISTING_CONFIRM_CODE_COPY)
          });
          if (!error?.response) {
            throw error;
          }
        });
    };
  },

  handleVerifyPhone(code, pathname, phone) {
    return (dispatch, getState) => {
      const {
        listing: { memberData, notificationPhone }
      } = getState();

      dispatch(this.setLoadingNotifications(true));

      return apiClient({
        method: "post",
        responseType: "json",
        url: "members/api-v2/verification/listing/phone/confirm-code",
        headers: { "Content-Type": "application/json" },
        data: {
          code,
          token: notificationPhone.token,
          phone: phone || notificationPhone.phone,
          listing_id: memberData.listing_id
        }
      })
        .then(() => dispatch(actions.getMemberDataNew(memberData.listing_id, pathname, null, false, { bustCache: true })))
        .then(member => {
          if (!member) {
            dispatch({
              type: listing.NOTIFICATION_VERIFICATION_PHONE_FAIL,
              payload: { saveError: "Error saving changes" }
            });
            return;
          }

          dispatch({
            type: listing.NOTIFICATION_VERIFICATION_PHONE_SUCCESS
          });
        })
        .catch(error => {
          dispatch({
            type: listing.NOTIFICATION_VERIFICATION_PHONE_FAIL,
            payload: listingConfirmCodeFailPayload(error, LISTING_CONFIRM_CODE_COPY)
          });
          if (!error?.response) {
            throw error;
          }
        });
    };
  },

  handleNotificationPhone(phone) {
    return (dispatch, getState) => {
      dispatch(this.setLoadingNotifications(true));

      const listingId = getState().listing?.memberData?.listing_id;

      // Returned so the modal can await the send and only then advance to the code step.
      return apiClient({
        method: "post",
        responseType: "json",
        url: "members/api-v2/verification/phone/send-code",
        headers: { "Content-Type": "application/json" },
        data: {
          phone,
          listing_id: listingId
        }
      })
        .then(({ data }) => {
          dispatch({
            type: listing.NOTIFICATION_PHONE,
            payload: { phone, token: data?.data?.token || "" }
          });
          return true;
        })
        .catch(error => {
          dispatch({
            type: listing.NOTIFICATION_PHONE_FAIL,
            payload: error?.response?.data?.data?.error || error?.response?.data?.message || "Failed to update phone"
          });
          return false;
        });
    };
  },

  getListingAssistant() {
    return dispatch => {
      apiClient({
        method: "get",
        responseType: "json",
        url: "members/json-api/listing-assistant/get"
      }).then(res => {
        dispatch({
          type: listing.GET_LISTING_ASSISTANT,
          payload: res.data?.records
        });
      });
    };
  },

  getConfig() {
    return dispatch => {
      dispatch(actions.setLoading("listingConfig", true));

      apiClient({
        method: "get",
        responseType: "json",
        url: "members/json-api/index/config"
      })
        .then(res => {
          const {
            events_profession: eventsProfession,
            jobs_profession: jobsProfession,
            profiles_form: profilesForm,
            profiles_profession: profilesProfession,
            business_profession: businessProfession
          } = res.data.listings;
          dispatch({
            type: listing.GET_LISTINGS_CONFIG,
            payload: {
              eventsProfession,
              jobsProfession,
              profilesForm,
              profilesProfession,
              businessProfession
            }
          });
        })
        .catch(error => {
          dispatch({
            type: listing.GET_LISTINGS_CONFIG_FAIL,
            payload: error.response?.data?.error
          });
        });
    };
  },

  setSuccess(action, isActionSuccessful) {
    return {
      type: listing.SET_SUCCESS,
      payload: { action, isActionSuccessful }
    };
  },

  setLoadingNotifications(isLoading) {
    return {
      type: listing.NOTIFICATIONS_LOADING,
      payload: isLoading
    };
  },

  setValidationError(error) {
    return {
      type: listing.SET_VALIDATION_ERROR,
      payload: error
    };
  },

  /**
   * Loads a listing's notification preferences, seeding the defaults on first visit.
   *
   * A team member who may not write (View-Only) is shown the same defaults without saving them: api-v2-team refuses the
   * seed, and the rows get a local id because nothing is stored yet (GROWTH-7138).
   *
   * @param {number|string} listingId
   * @param {"events"|"jobs"|"default"} [listingKind="default"] Events and Jobs are seeded without the lead categories.
   */
  getNotificationTypes(listingId, listingKind = "default") {
    return (dispatch, getState) => {
      const url = buildListingApiUrl("/notification-types", getState, { listing_id: listingId });
      let defaultsChainOwnsPreloader = false;

      dispatch(actions.setLoading("notificationTypes", true));

      apiClient({
        method: "get",
        responseType: "json",
        url,
        headers: {
          "Content-Type": "application/json"
        }
      })
        .then(res => {
          const notificationTypes = res.data.data;

          if (Array.isArray(notificationTypes) && !notificationTypes.length) {
            if (!canWriteListingsInTeamContext(getState)) {
              dispatch({
                type: listing.GET_NOTIFICATION_TYPES_SUCCESS,
                payload: buildDefaultNotificationRows(listingId, listingKind).map(row => ({ ...row, id: `default-${row.category}` }))
              });
              return;
            }

            const requestKey = `notification-defaults:${listingId}`;
            const defaultsRequest =
              pendingNotificationDefaultRequests.get(requestKey) ||
              dispatch(actions.createNotificationType(buildDefaultNotificationRows(listingId, listingKind), true));

            if (!pendingNotificationDefaultRequests.has(requestKey)) {
              pendingNotificationDefaultRequests.set(requestKey, defaultsRequest);
            }

            defaultsChainOwnsPreloader = true;

            defaultsRequest
              .then(result => {
                const createdTypes = Array.isArray(result?.data) ? result.data : [];
                dispatch({
                  type: listing.GET_NOTIFICATION_TYPES_SUCCESS,
                  payload: createdTypes
                });
              })
              .catch(error => {
                dispatch({
                  type: listing.GET_NOTIFICATION_TYPES_FAIL,
                  payload: error.response?.data || error.message
                });
              })
              .finally(() => {
                pendingNotificationDefaultRequests.delete(requestKey);
                dispatch(actions.setLoading("notificationTypes", false));
              });
          } else {
            dispatch({
              type: listing.GET_NOTIFICATION_TYPES_SUCCESS,
              payload: notificationTypes
            });
          }
        })
        .catch(error => {
          dispatch({
            type: listing.GET_NOTIFICATION_TYPES_FAIL,
            payload: error.response?.data || error.message
          });
        })
        .finally(() => {
          if (!defaultsChainOwnsPreloader) {
            dispatch(actions.setLoading("notificationTypes", false));
          }
        });
    };
  },

  getDirectMessages(listingId) {
    return (dispatch, getState) => {
      const url = buildListingApiUrl("/direct-messages", getState, { listing_id: listingId });

      dispatch(actions.setLoading("directMessages", true));

      apiClient({
        method: "get",
        responseType: "json",
        url,
        headers: {
          "Content-Type": "application/json"
        }
      })
        .then(res => {
          const directMessages = normalizeDirectMessages(res.data?.data, listingId);
          dispatch({
            type: listing.GET_DIRECT_MESSAGES_SUCCESS,
            payload: directMessages
          });
        })
        .catch(error => {
          dispatch({
            type: listing.GET_DIRECT_MESSAGES_FAIL,
            payload: error?.response?.data || error.message
          });
        })
        .finally(() => {
          dispatch(actions.setLoading("directMessages", false));
        });
    };
  },

  getDonationsSettings(listingId) {
    return (dispatch, getState) => {
      const url = buildListingApiUrl("/donations/settings", getState, { listing_id: listingId });

      dispatch(actions.setLoading("donationsSettings", true));
      dispatch(actions.getDonationsActivity(listingId));

      apiClient({
        method: "get",
        responseType: "json",
        url,
        headers: {
          "Content-Type": "application/json"
        }
      })
        .then(res => {
          dispatch({
            type: listing.GET_DONATIONS_SETTINGS_SUCCESS,
            payload: res?.data?.data || null
          });
        })
        .catch(error => {
          dispatch({
            type: listing.GET_DONATIONS_SETTINGS_FAIL,
            payload: error?.response?.data || error.message
          });
        })
        .finally(() => {
          dispatch(actions.setLoading("donationsSettings", false));
        });
    };
  },

  getDonationsActivity(listingId) {
    return (dispatch, getState) => {
      const url = buildListingApiUrl("/donations/activity", getState, { listing_id: listingId });

      dispatch(actions.setLoading("donationsActivity", true));

      return apiClient({
        method: "get",
        responseType: "json",
        url,
        headers: {
          "Content-Type": "application/json"
        }
      })
        .then(res => {
          dispatch({
            type: listing.GET_DONATIONS_ACTIVITY_SUCCESS,
            payload: res?.data?.data || null
          });
        })
        .catch(error => {
          dispatch({
            type: listing.GET_DONATIONS_ACTIVITY_FAIL,
            payload: error?.response?.data || error.message
          });
        })
        .finally(() => {
          dispatch(actions.setLoading("donationsActivity", false));
        });
    };
  },

  getListingSectionHide(listingId) {
    return (dispatch, getState) => {
      const url = buildListingApiUrl("/listing-section-hide", getState, { listing_id: listingId });

      dispatch(actions.setLoading("listingSectionHide", true));

      apiClient({
        method: "get",
        responseType: "json",
        url,
        headers: {
          "Content-Type": "application/json"
        }
      })
        .then(res => {
          const sectionHideData = res.data.data;

          dispatch({
            type: listing.GET_LISTING_SECTION_HIDE_SUCCESS,
            payload: sectionHideData
          });
        })
        .catch(error => {
          dispatch({
            type: listing.GET_LISTING_SECTION_HIDE_FAIL,
            payload: error?.response?.data || error.message
          });
        })
        .finally(() => {
          dispatch(actions.setLoading("listingSectionHide", false));
        });
    };
  },

  /**
   * The publications that fit a listing's communities and industries, plus the ones it is in (GROWTH-7889, Publications block).
   * Resolves to `[{ dir_id, name, url, page_url, community, matched, picked, public, is_default, review_status, can_remove,
   * was_removed, social }]`; rejects with the server's message.
   */
  getListingPublications(listingId) {
    return (dispatch, getState) => {
      const url = buildListingApiUrl("/listing-publications", getState, { listing_id: listingId });

      return apiClient({ method: "get", responseType: "json", url, headers: { "Content-Type": "application/json" } })
        .then(res => res.data.data || [])
        .catch(error => Promise.reject(new Error(error?.response?.data?.error || error?.message || "")));
    };
  },

  /**
   * The checkbox (GROWTH-7951): turns a publication on or off in place, without deleting it — a reversible "temporary hide"
   * that keeps it listed. Turning on a publication with no row yet is still a fresh pick (matched-gated, team-reviewed).
   * Resolves to the refreshed list, as getListingPublications does.
   */
  saveListingPublication(listingId, dirId, pick) {
    return (dispatch, getState) => {
      const { apiPrefix } = resolveListingTeamContext(getState);

      return apiClient({
        method: "post",
        responseType: "json",
        url: `${apiPrefix}/listing-publications`,
        data: withListingTeamBody(getState, { listing_id: listingId, dir_id: dirId, publish: pick ? 1 : 0 }),
        headers: { "Content-Type": "application/json" }
      })
        .then(res => res.data.data || [])
        .catch(error => Promise.reject(new Error(error?.response?.data?.error || error?.message || "")));
    };
  },

  /**
   * Delete (GROWTH-7951 round four): permanently removes a publication, distinct from the checkbox above — it deletes the
   * row entirely, and if the publication still matches the listing's communities and industries, it reappears under
   * "Suggested for you" (`was_removed`) to be added again. Not available for heritageweb.com. Resolves to the refreshed list.
   * The ids go in the query string: apiv2's getRequestParams() reads a request body only for POST and PUT.
   */
  deleteListingPublication(listingId, dirId) {
    return (dispatch, getState) => {
      const { apiPrefix } = resolveListingTeamContext(getState);

      return apiClient({
        method: "delete",
        responseType: "json",
        url: `${apiPrefix}/listing-publications`,
        params: withListingTeamBody(getState, { listing_id: listingId, dir_id: dirId })
      })
        .then(res => res.data.data || [])
        .catch(error => Promise.reject(new Error(error?.response?.data?.error || error?.message || "")));
    };
  },

  updateListingSectionHide(listingId, listingSectionId, hidden) {
    return (dispatch, getState) => {
      const { apiPrefix } = resolveListingTeamContext(getState);
      const url = `${apiPrefix}/listing-section-hide`;

      dispatch(actions.setLoading("listingSectionHide", true));

      apiClient({
        method: "post",
        responseType: "json",
        url,
        data: withListingTeamBody(getState, {
          listing_id: listingId,
          listing_section_id: listingSectionId,
          hidden: hidden ? 1 : 0
        }),
        headers: {
          "Content-Type": "application/json"
        }
      })
        .then(res => {
          const sectionHideData = res.data.data || [];

          dispatch({
            type: listing.UPDATE_LISTING_SECTION_HIDE_SUCCESS,
            payload: sectionHideData
          });
        })
        .catch(error => {
          dispatch({
            type: listing.UPDATE_LISTING_SECTION_HIDE_FAIL,
            payload: error?.response?.data || error.message
          });
        })
        .finally(() => {
          dispatch(actions.setLoading("listingSectionHide", false));
        });
    };
  },

  /**
   * One call to /notification-routing (GROWTH-7943) in the listing's team context. Resolves with the page payload (`data.data`) and
   * rejects with the axios error, so the Notifications blocks decide what to roll back. Reads send `query`; writes send `body` (the
   * team's user_id is added to either).
   * `resource` picks another notification endpoint (the Send By switches write /notification-types).
   * @param {{ method?: string, resource?: string, path?: string, query?: Object, body?: Object }} request
   */
  notificationRoutingRequest({ method = "get", resource = "notification-routing", path = "", query = {}, body } = {}) {
    return (dispatch, getState) =>
      apiClient({
        method,
        responseType: "json",
        url: buildListingApiUrl(`/${resource}${path}`, getState, method === "get" ? query : {}),
        data: method === "get" ? undefined : withListingTeamBody(getState, body),
        headers: { "Content-Type": "application/json" }
      }).then(res => res.data.data);
  },

  updateNotificationType(id, data) {
    return (dispatch, getState) => {
      const { id: _ignoredId, ...patch } = data || {};
      const changedFields = Object.keys(patch);
      const previousRow = (getState().listing?.memberData?.notificationTypes || []).find(item => String(item.id) === String(id));
      const previous = changedFields.reduce((acc, field) => {
        acc[field] = previousRow ? previousRow[field] : undefined;
        return acc;
      }, {});

      const controller = new AbortController();
      changedFields.forEach(field => {
        const requestKey = notificationTypeFieldKey(id, field);
        const inFlight = inFlightNotificationTypeRequests.get(requestKey);
        if (inFlight) {
          inFlight.abort();
        }
        inFlightNotificationTypeRequests.set(requestKey, controller);
      });

      dispatch({ type: listing.UPDATE_NOTIFICATION_TYPE, payload: { id, patch } });
      dispatch({
        type: listing.SET_LOADING,
        payload: { section: "updateNotificationType", isSectionLoading: true }
      });

      return apiClient({
        method: "put",
        responseType: "json",
        url: buildListingApiUrl("/notification-types/index", getState, { id }),
        data: withListingTeamBody(getState, patch),
        signal: controller.signal,
        headers: { "Content-Type": "application/json" }
      })
        .then(res => {
          dispatch({
            type: listing.UPDATE_NOTIFICATION_TYPE_SUCCESS,
            payload: { row: res.data.data, fields: changedFields }
          });
        })
        .catch(error => {
          if (isCanceledRequest(error)) {
            return;
          }
          dispatch({
            type: listing.UPDATE_NOTIFICATION_TYPE_ROLLBACK,
            payload: { id, previous, fields: changedFields, error: notificationTypeRollbackError(error) }
          });
        })
        .finally(() => {
          let successorInFlight = false;
          changedFields.forEach(field => {
            const requestKey = notificationTypeFieldKey(id, field);
            if (inFlightNotificationTypeRequests.get(requestKey) === controller) {
              inFlightNotificationTypeRequests.delete(requestKey);
            } else if (inFlightNotificationTypeRequests.has(requestKey)) {
              successorInFlight = true;
            }
          });
          if (!successorInFlight) {
            dispatch({
              type: listing.SET_LOADING,
              payload: { section: "updateNotificationType", isSectionLoading: false }
            });
          }
        });
    };
  },

  saveDirectMessages(data) {
    return (dispatch, getState) => {
      const hasId = Boolean(data?.id);
      const { apiPrefix } = resolveListingTeamContext(getState);
      const url = hasId ? buildListingApiUrl("/direct-messages/index", getState, { id: data.id }) : `${apiPrefix}/direct-messages`;
      const method = hasId ? "put" : "post";
      const payload = {
        listing_id: data?.listing_id,
        is_enabled: data?.is_enabled ? 1 : 0,
        email_verification: data?.email_verification ? 1 : 0,
        phone_requirement: data?.phone_requirement || "optional",
        phone_verification: data?.phone_verification ? 1 : 0
      };

      dispatch(actions.setLoading("updateDirectMessages", true));

      return apiClient({
        method,
        responseType: "json",
        url,
        data: withListingTeamBody(getState, payload),
        headers: {
          "Content-Type": "application/json"
        }
      })
        .then(res => {
          const normalized = normalizeDirectMessages(res.data?.data, data?.listing_id ?? payload.listing_id);
          dispatch({
            type: listing.UPDATE_DIRECT_MESSAGES_SUCCESS,
            payload: normalized
          });

          return {
            ...res.data,
            data: normalized
          };
        })
        .catch(error => {
          dispatch({
            type: listing.UPDATE_DIRECT_MESSAGES_FAIL,
            payload: error?.response?.data || error.message
          });
          throw error;
        })
        .finally(() => {
          dispatch(actions.setLoading("updateDirectMessages", false));
        });
    };
  },

  requestDonations(listingId) {
    return (dispatch, getState) => {
      const { apiPrefix } = resolveListingTeamContext(getState);
      const url = `${apiPrefix}/donations/request`;

      dispatch(actions.setLoading("donationsRequest", true));

      return apiClient({
        method: "post",
        responseType: "json",
        url,
        data: withListingTeamBody(getState, {
          listing_id: listingId
        }),
        headers: {
          "Content-Type": "application/json"
        }
      })
        .then(res => {
          dispatch(actions.getDonationsSettings(listingId));
          return res?.data;
        })
        .catch(error => {
          dispatch({
            type: listing.GET_DONATIONS_SETTINGS_FAIL,
            payload: error?.response?.data || error.message
          });
          throw error;
        })
        .finally(() => {
          dispatch(actions.setLoading("donationsRequest", false));
        });
    };
  },

  connectDonationsStripe(listingId) {
    return (dispatch, getState) => {
      const { apiPrefix } = resolveListingTeamContext(getState);
      const url = `${apiPrefix}/donations/connect-stripe`;

      dispatch(actions.setLoading("donationsConnectStripe", true));

      // A failure is rethrown for the caller to show inline. It does not dispatch GET_DONATIONS_SETTINGS_FAIL: the
      // reducer answers that by replacing donationsSettings with "not eligible", which swaps the page for the
      // nonprofit card while the member's Stripe connection is untouched.
      return apiClient({
        method: "post",
        responseType: "json",
        url,
        data: withListingTeamBody(getState, {
          listing_id: listingId
        }),
        headers: {
          "Content-Type": "application/json"
        }
      })
        .then(res => {
          return res?.data;
        })
        .finally(() => {
          dispatch(actions.setLoading("donationsConnectStripe", false));
        });
    };
  },

  disconnectDonationsStripe(listingId) {
    return (dispatch, getState) => {
      const url = buildListingApiUrl("/donations/connect-stripe", getState, { listing_id: listingId });

      dispatch(actions.setLoading("donationsDisconnectStripe", true));

      // Rethrown, not GET_DONATIONS_SETTINGS_FAIL: see connectDonationsStripe.
      return apiClient({
        method: "delete",
        responseType: "json",
        url,
        data: withListingTeamBody(getState, {
          listing_id: listingId
        }),
        headers: {
          "Content-Type": "application/json"
        }
      })
        .then(res => {
          dispatch(actions.getDonationsSettings(listingId));
          return res?.data;
        })
        .finally(() => {
          dispatch(actions.setLoading("donationsDisconnectStripe", false));
        });
    };
  },

  createNotificationType(data, skipDispatch = false) {
    return (dispatch, getState) => {
      const { apiPrefix } = resolveListingTeamContext(getState);
      const url = `${apiPrefix}/notification-types`;
      const payload = Array.isArray(data) ? data.map(item => withListingTeamBody(getState, item)) : withListingTeamBody(getState, data);

      if (!skipDispatch) {
        dispatch(actions.setLoading("createNotificationType", true));
      }

      return apiClient({
        method: "post",
        responseType: "json",
        url,
        data: payload,
        headers: {
          "Content-Type": "application/json"
        }
      })
        .then(res => {
          if (!skipDispatch) {
            const responseData = res?.data?.data;
            dispatch({
              type: listing.CREATE_NOTIFICATION_TYPE_SUCCESS,
              payload: Array.isArray(responseData) ? responseData : [responseData]
            });
          }
          return res.data;
        })
        .catch(error => {
          if (!skipDispatch) {
            dispatch({
              type: listing.CREATE_NOTIFICATION_TYPE_FAIL,
              payload: error.response?.data || error.message
            });
          }
          throw error;
        })
        .finally(() => {
          if (!skipDispatch) {
            dispatch(actions.setLoading("createNotificationType", false));
          }
        });
    };
  },

  resetNotificationState() {
    return dispatch => {
      dispatch({
        type: listing.RESET_NOTIFICATION_STATE
      });
    };
  },

  // ============================================
  // AI Content Generation Actions
  // ============================================

  /**
   * Generate AI description for a listing
   * @param {number} listingId - The listing ID
   * @param {number} directoryId - The directory ID
   * @param {number} languageId - Optional language ID (defaults to English)
   * @param {boolean} forceRegenerate - Force regeneration even if content exists
   */
  generateAiDescription(listingId, directoryId, languageId = null, forceRegenerate = false) {
    return (dispatch, getState) => {
      dispatch({ type: listing.AI_GENERATE_DESCRIPTION });

      const data = {
        listing_id: listingId,
        directory_id: directoryId,
        force_regenerate: forceRegenerate
      };

      if (languageId) {
        data.language_id = languageId;
      }

      const { apiPrefix } = resolveListingTeamContext(getState);

      return apiClient({
        method: "post",
        responseType: "json",
        url: `${apiPrefix}/ai-content/generate-description`,
        data: withListingTeamBody(getState, data),
        headers: { "Content-Type": "application/json" }
      })
        .then(res => {
          dispatch({
            type: listing.AI_GENERATE_DESCRIPTION_SUCCESS,
            payload: res.data.data
          });
          return res.data;
        })
        .catch(error => {
          const errorMessage = error?.response?.data?.errors?.[0] || error?.response?.data?.message || "Failed to generate description";
          const errorData = error?.response?.data?.data || null;

          dispatch({
            type: listing.AI_GENERATE_DESCRIPTION_FAIL,
            payload: { message: errorMessage, data: errorData }
          });
          throw error;
        });
    };
  },

  /**
   * Generate AI translation for a listing
   * @param {number} listingId - The listing ID
   * @param {number} directoryId - The directory ID
   * @param {number} languageId - Target language ID
   * @param {boolean} forceRegenerate - Force regeneration even if translation exists
   */
  generateAiTranslation(listingId, directoryId, languageId, forceRegenerate = false) {
    return (dispatch, getState) => {
      dispatch({ type: listing.AI_GENERATE_TRANSLATION });

      const { apiPrefix } = resolveListingTeamContext(getState);

      return apiClient({
        method: "post",
        responseType: "json",
        url: `${apiPrefix}/ai-content/generate-translation`,
        data: withListingTeamBody(getState, {
          listing_id: listingId,
          directory_id: directoryId,
          language_id: languageId,
          force_regenerate: forceRegenerate
        }),
        headers: { "Content-Type": "application/json" }
      })
        .then(res => {
          dispatch({
            type: listing.AI_GENERATE_TRANSLATION_SUCCESS,
            payload: res.data.data
          });
          return res.data;
        })
        .catch(error => {
          const errorMessage = error?.response?.data?.errors?.[0] || error?.response?.data?.message || "Failed to generate translation";
          const errorData = error?.response?.data?.data || null;

          dispatch({
            type: listing.AI_GENERATE_TRANSLATION_FAIL,
            payload: { message: errorMessage, data: errorData }
          });
          throw error;
        });
    };
  },

  /**
   * Get job status for polling
   * @param {string} jobId - The job ID
   */
  getAiJobStatus(jobId) {
    return (dispatch, getState) => {
      dispatch({ type: listing.AI_GET_JOB_STATUS });

      return apiClient({
        method: "get",
        responseType: "json",
        url: buildListingApiUrl(`/ai-content/job-status/${jobId}`, getState),
        headers: { "Content-Type": "application/json" }
      })
        .then(res => {
          dispatch({
            type: listing.AI_GET_JOB_STATUS_SUCCESS,
            payload: res.data.data
          });
          return res.data;
        })
        .catch(error => {
          dispatch({
            type: listing.AI_GET_JOB_STATUS_FAIL,
            payload: error?.response?.data?.errors?.[0] || "Failed to get job status"
          });
          throw error;
        });
    };
  },

  /**
   * Get AI-generated content for a listing
   * @param {number} listingId - The listing ID
   * @param {number} directoryId - The directory ID
   * @param {number} languageId - Optional language ID
   */
  getAiListingContent(listingId, directoryId, languageId = null) {
    return (dispatch, getState) => {
      dispatch({ type: listing.AI_GET_LISTING_CONTENT });

      const query = { listing_id: listingId, directory_id: directoryId };
      if (languageId) {
        query.language_id = languageId;
      }
      const url = buildListingApiUrl("/ai-content/listing-content", getState, query);

      return apiClient({
        method: "get",
        responseType: "json",
        url,
        headers: { "Content-Type": "application/json" }
      })
        .then(res => {
          dispatch({
            type: listing.AI_GET_LISTING_CONTENT_SUCCESS,
            payload: res.data.data
          });
          return res.data;
        })
        .catch(error => {
          dispatch({
            type: listing.AI_GET_LISTING_CONTENT_FAIL,
            payload: error?.response?.data?.errors?.[0] || "No AI content found"
          });
          // Don't throw - no content is a valid state
          return null;
        });
    };
  },

  /**
   * Set generated content to be applied to form
   * @param {object} content - The generated content (description, name, tagline)
   */
  setAiGeneratedContent(content) {
    return {
      type: listing.AI_SET_GENERATED_CONTENT,
      payload: content
    };
  },

  /**
   * Clear AI state
   */
  clearAiState() {
    return {
      type: listing.AI_CLEAR_STATE
    };
  },

  /**
   * Clear Errors
   */
  clearErrorState() {
    return {
      type: listing.CLEAR_ERROR
    };
  },

  clearPhotoError() {
    return {
      type: listing.CLEAR_PHOTO_ERROR
    };
  }
};
